/**
 * Generated by orval v8.5.0 🍺
 * Do not edit manually.
 * WorkOS Widgets
 * WorkOS Widgets API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import { useCallback } from "react";

import { useWidgetsApiClient } from "./widgets-api-client.js";
import type { ErrorType, BodyType } from "./widgets-api-client.js";
import { useWidgetsApiQueryOptions } from "./widgets-api-client.js";
export type MemberStatus = (typeof MemberStatus)[keyof typeof MemberStatus];

export const MemberStatus = {
  Active: "Active",
  Invited: "Invited",
  InviteExpired: "InviteExpired",
  InviteRevoked: "InviteRevoked",
  NoInvite: "NoInvite",
} as const;

export type MemberActionsItem =
  (typeof MemberActionsItem)[keyof typeof MemberActionsItem];

export const MemberActionsItem = {
  "edit-role": "edit-role",
  "resend-invite": "resend-invite",
  "revoke-invite": "revoke-invite",
  "revoke-membership": "revoke-membership",
} as const;

export type MemberActions = MemberActionsItem[];

export type MemberRoles =
  | {
      name: string;
      slug: string;
      /** @nullable */
      description?: string | null;
    }[]
  | null;

export interface Member {
  id: string;
  email: string;
  emailVerified: boolean;
  /** @nullable */
  profilePictureUrl?: string | null;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** An ISO 8601 timestamp. */
  createdAt: string;
  /** @nullable */
  lastActivityAt?: string | null;
  status: MemberStatus;
  actions: MemberActions;
  isLoggedInUser?: true | null;
  roles?: MemberRoles;
}

/**
 * Pagination cursors for navigating between pages of results.
 */
export interface ListMetadata {
  /**
   * An object ID that defines your place in the list. When the ID is not present, you are at the start of the list.
   * @nullable
   */
  before: string | null;
  /**
   * An object ID that defines your place in the list. When the ID is not present, you are at the end of the list.
   * @nullable
   */
  after: string | null;
}

export interface MembersResponse {
  data: Member[];
  list_metadata: ListMetadata;
}

export interface MemberRole {
  name: string;
  slug: string;
  default: boolean;
  /** @nullable */
  description?: string | null;
}

export interface RolesAndConfigResponse {
  roles: MemberRole[];
  multipleRolesEnabled: boolean;
}

export interface InviteMemberResponse {
  success: boolean;
}

export interface ResendInviteResponse {
  /** @nullable */
  id?: string | null;
  success: boolean;
}

export interface RevokeMemberResponse {
  id: string;
  success: boolean;
}

export interface UpdateMemberResponse {
  id: string;
  success: boolean;
}

export interface RemoveMemberResponse {
  id: string;
  success: boolean;
}

export interface OrganizationInfo {
  id: string;
  name: string;
  current: boolean;
  /** @nullable */
  favicon?: string | null;
}

export interface OrganizationsResponse {
  data: OrganizationInfo[];
}

export interface OAuthProfile {
  id: string;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** @nullable */
  profilePictureUrl?: string | null;
  /** @nullable */
  lastLoginAt?: string | null;
}

export type MeOauthProfiles = {
  AppleOAuth?: OAuthProfile;
  GithubOAuth?: OAuthProfile;
  GoogleOAuth?: OAuthProfile;
  MicrosoftOAuth?: OAuthProfile;
} | null;

export interface Me {
  id: string;
  email: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** @nullable */
  locale?: string | null;
  /** @nullable */
  profilePictureUrl?: string | null;
  oauthProfiles?: MeOauthProfiles;
}

export type CreateTotpFactorResponseAuthenticationFactor = {
  object: "authentication_factor";
  id: string;
  type: "generic_otp" | "sms" | "totp" | "webauthn";
  /** @nullable */
  user_id?: string | null;
  sms?: {
    phone_number: string;
  } | null;
  totp?:
    | {
        issuer: string;
        user: string;
        secret: string;
        qr_code: string;
        uri: string;
      }
    | {
        issuer: string;
        user: string;
      }
    | null;
} & {
  created_at: string;
  updated_at: string;
};

export type CreateTotpFactorResponseAuthenticationChallenge = {
  object: "authentication_challenge";
  id: string;
  /** @nullable */
  expires_at?: string | null;
  /** @nullable */
  code?: string | null;
  authentication_factor_id: string;
} & {
  created_at: string;
  updated_at: string;
};

export interface CreateTotpFactorResponse {
  authenticationFactor: CreateTotpFactorResponseAuthenticationFactor;
  authenticationChallenge: CreateTotpFactorResponseAuthenticationChallenge;
}

export interface VerifyTotpFactorRequest {
  code: string;
  authenticationChallengeId: string;
}

export type AuthenticationInformationResponseDataVerificationMethodsMfa = {
  provider: "MFA";
  isSetUp: boolean;
  /** @nullable */
  lastUsed?: string | null;
} | null;

export type AuthenticationInformationResponseDataVerificationMethodsPassword = {
  provider: "Password";
  isSetUp: boolean;
  /** @nullable */
  lastUsed?: string | null;
  isCurrentSession: boolean;
} | null;

export type AuthenticationInformationResponseDataVerificationMethodsPasskey = {
  provider: "Passkey";
  isSetUp: boolean;
  /** @nullable */
  lastUsed?: string | null;
  passKeys: {
    id: string;
  }[];
  isCurrentSession: boolean;
} | null;

export type AuthenticationInformationResponseDataVerificationMethods = {
  Mfa?: AuthenticationInformationResponseDataVerificationMethodsMfa;
  Password?: AuthenticationInformationResponseDataVerificationMethodsPassword;
  Passkey?: AuthenticationInformationResponseDataVerificationMethodsPasskey;
};

export type AuthenticationInformationResponseDataPasswordSettings = {
  isPasswordNumberRequired: boolean;
  isPasswordPwnedRequired: boolean;
  isPasswordSymbolRequired: boolean;
  isPasswordUppercaseRequired: boolean;
  passwordMinimumLength: number;
  passwordMinimumStrength: number;
};

export type AuthenticationInformationResponseData = {
  verificationMethods: AuthenticationInformationResponseDataVerificationMethods;
  passwordSettings: AuthenticationInformationResponseDataPasswordSettings;
};

export interface AuthenticationInformationResponse {
  data: AuthenticationInformationResponseData;
}

export interface CreatePasswordRequest {
  password: string;
}

export interface UpdatePasswordRequest {
  newPassword: string;
  currentPassword: string;
}

export interface RevokeAllSessionsRequest {
  currentSessionId: string;
}

export type ActiveSessionState = {
  tag: string;
  /** @nullable */
  expiresAt?: string | null;
};

export type ActiveSessionCurrentLocation = {
  cityName: string;
  countryISOCode: string;
} | null;

export interface ActiveSession {
  id: string;
  userlandUserId: string;
  /** @nullable */
  ipAddress?: string | null;
  /** @nullable */
  userAgent?: string | null;
  /** @nullable */
  organizationId?: string | null;
  state: ActiveSessionState;
  currentLocation?: ActiveSessionCurrentLocation;
  usedSsoAuth: boolean;
  usedPasswordAuth: boolean;
  usedPasskeyAuth: boolean;
  usedAppleOauth: boolean;
  usedBitbucketOauth: boolean;
  usedGithubOauth: boolean;
  usedGitLabOauth: boolean;
  usedGoogleOauth: boolean;
  usedLinkedInOauth: boolean;
  usedImpersonation: boolean;
  usedMicrosoftOauth: boolean;
  usedSlackOauth: boolean;
  usedXeroOauth: boolean;
  usedMagicAuth: boolean;
  /** @nullable */
  impersonatorUserId?: string | null;
  /** @nullable */
  impersonatorEmail?: string | null;
  /** @nullable */
  impersonationReason?: string | null;
  /** @nullable */
  lastActivityAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActiveSessionsResponse {
  data: ActiveSession[];
}

export interface SendVerificationResponse {
  authenticationChallenge: string;
  type: "EmailVerification";
}

export interface VerifyRequest {
  code: string;
  authenticationChallengeId: string;
}

export interface VerifyResponse {
  elevatedAccessToken: string;
  expiresAt: string;
}

export interface SendEmailChangeRequest {
  newEmail: string;
}

export interface SendEmailChangeResponse {
  expiresAt: string;
}

export interface VerifyEmailChangeRequest {
  code: string;
}

export interface VerifyEmailChangeResponse {
  user: Me;
  elevatedAccessToken: string;
  expiresAt: string;
}

export type RegisterPasskeyResponseOptions = { [key: string]: unknown };

export interface RegisterPasskeyResponse {
  challengeId: string;
  options: RegisterPasskeyResponseOptions;
}

export type VerifyPasskeyRequestResponse = { [key: string]: unknown };

export interface VerifyPasskeyRequest {
  challengeId: string;
  response: VerifyPasskeyRequestResponse;
}

export interface SettingsResponse {
  object: "settings";
  authkitOrigin: string;
  /** @nullable */
  logoDarkIconPath?: string | null;
  /** @nullable */
  logoDarkPath?: string | null;
  /** @nullable */
  logoLightIconPath?: string | null;
  /** @nullable */
  logoLightPath?: string | null;
  teamName: string;
}

export type OrganizationDomainState =
  (typeof OrganizationDomainState)[keyof typeof OrganizationDomainState];

export const OrganizationDomainState = {
  Failed: "Failed",
  LegacyVerified: "LegacyVerified",
  Pending: "Pending",
  Verified: "Verified",
} as const;

export type DomainVerificationNameServer =
  (typeof DomainVerificationNameServer)[keyof typeof DomainVerificationNameServer];

export const DomainVerificationNameServer = {
  AwsRoute53: "AwsRoute53",
  GoogleDomains: "GoogleDomains",
  CloudFlare: "CloudFlare",
  GoDaddy: "GoDaddy",
  Other: "Other",
} as const;

export interface OrganizationDomain {
  id: string;
  domain: string;
  state: OrganizationDomainState;
  nameServer: DomainVerificationNameServer;
  /** @nullable */
  verificationPrefix?: string | null;
  /** @nullable */
  verificationToken?: string | null;
  /** @nullable */
  subdomain?: string | null;
  createdAt: string;
}

export interface X509CertificateJSON {
  id: string;
  value: string;
  /** @nullable */
  notBefore?: string | null;
  /** @nullable */
  notAfter?: string | null;
  /** @nullable */
  lastExpiryEventSentAt?: string | null;
}

export type SamlSessionState =
  (typeof SamlSessionState)[keyof typeof SamlSessionState];

export const SamlSessionState = {
  Authorized: "Authorized",
  Failed: "Failed",
  Started: "Started",
  Successful: "Successful",
  Timedout: "Timedout",
} as const;

export type OidcSessionState =
  (typeof OidcSessionState)[keyof typeof OidcSessionState];

export const OidcSessionState = {
  Started: "Started",
  Authorized: "Authorized",
  Successful: "Successful",
  Failed: "Failed",
  Terminated: "Terminated",
  Timedout: "Timedout",
} as const;

export const SsoConnectionSessionJSONState = {
  ...SamlSessionState,
  ...OidcSessionState,
} as const;
export interface SsoConnectionSessionJSON {
  id: string;
  createdAt: string;
  state: (typeof SsoConnectionSessionJSONState)[keyof typeof SsoConnectionSessionJSONState];
}

export type SsoConnection =
  | {
      id: string;
      type:
        | "AdfsSaml"
        | "Auth0Saml"
        | "AzureSaml"
        | "CasSaml"
        | "ClassLinkSaml"
        | "CloudflareSaml"
        | "CyberArkSaml"
        | "DuoSaml"
        | "GenericSaml"
        | "GoogleSaml"
        | "JumpCloudSaml"
        | "KeycloakSaml"
        | "LastPassSaml"
        | "MiniOrangeSaml"
        | "NetIqSaml"
        | "OktaSaml"
        | "OneLoginSaml"
        | "OracleSaml"
        | "PingFederateSaml"
        | "PingOneSaml"
        | "RipplingSaml"
        | "SalesforceSaml"
        | "ShibbolethGenericSaml"
        | "ShibbolethSaml"
        | "SimpleSamlPhpSaml"
        | "TestIdp"
        | "VmWareSaml";
      name: string;
      state: "Inactive" | "Validating" | "Active" | "Deleting";
      x509Certificates: X509CertificateJSON[];
      latestExpiringCertificate?: X509CertificateJSON | null;
      latestExpiredCertificate?: X509CertificateJSON | null;
      createdAt: string;
      providerTag: "Saml";
      lastSession?: SsoConnectionSessionJSON | null;
    }
  | ({
      id: string;
      name: string;
      state: "Inactive" | "Validating" | "Active" | "Deleting";
      type:
        | "AdpOidc"
        | "Auth0Migration"
        | "CleverOidc"
        | "EntraIdOidc"
        | "GenericOidc"
        | "GoogleOidc"
        | "OktaOidc"
        | "LoginGovOidc";
      createdAt: string;
      providerTag: "OpenIdConnect";
      lastSession?: SsoConnectionSessionJSON | null;
    } & {
      /** @nullable */
      x509Certificates?: null;
      /** @nullable */
      latestExpiringCertificate?: null;
      /** @nullable */
      latestExpiredCertificate?: null;
    });

export type AuditLogStreamState = "Active" | "Inactive" | "Error" | "Invalid";

export type AuditLogStreamType =
  | "Datadog"
  | "Splunk"
  | "S3"
  | "GoogleCloudStorage"
  | "GenericHttps"
  | "AzureSentinel";

export interface AuditLogStreamJSON {
  id: string;
  state: AuditLogStreamState;
  type: AuditLogStreamType;
  /** @nullable */
  errorMessage?: string | null;
  /** @nullable */
  lastSyncedEventId?: string | null;
  auditLogTrailId: string;
}

export interface ListOrganizationApiKeysResponseData {
  id: string;
  name: string;
  obfuscatedValue: string;
  createdAt: string;
  /** @nullable */
  lastUsedAt?: string | null;
  permissions: string[];
}

export type ListOrganizationApiKeysResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListOrganizationApiKeysResponse {
  data: ListOrganizationApiKeysResponseData[];
  list_metadata: ListOrganizationApiKeysResponseListMetadata;
}

export interface CreateOrganizationApiKeyRequest {
  name: string;
  permissions: string[];
}

export interface CreateOrganizationApiKeyResponse {
  id: string;
  value: string;
  obfuscatedValue: string;
  createdAt: string;
  name: string;
  permissions: string[];
}

export interface ListOrganizationApiKeyPermission {
  id: string;
  slug: string;
  name: string;
  /** @nullable */
  description?: string | null;
}

export type ListOrganizationApiKeyPermissionsResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListOrganizationApiKeyPermissionsResponse {
  data: ListOrganizationApiKeyPermission[];
  list_metadata: ListOrganizationApiKeyPermissionsResponseListMetadata;
}

export type ListUserApiKeysResponseDataOwner = {
  type: "user";
  id: string;
  email: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  displayName: string;
};

export interface ListUserApiKeysResponseData {
  id: string;
  owner: ListUserApiKeysResponseDataOwner;
  name: string;
  obfuscatedValue: string;
  createdAt: string;
  /** @nullable */
  lastUsedAt?: string | null;
  permissions: string[];
}

export type ListUserApiKeysResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListUserApiKeysResponse {
  data: ListUserApiKeysResponseData[];
  list_metadata: ListUserApiKeysResponseListMetadata;
}

export interface CreateUserApiKeyRequest {
  name: string;
  permissions: string[];
}

export interface CreateUserApiKeyResponse {
  id: string;
  value: string;
  obfuscatedValue: string;
  createdAt: string;
  name: string;
  permissions: string[];
}

export interface ListUserApiKeyPermission {
  id: string;
  slug: string;
  name: string;
  /** @nullable */
  description?: string | null;
}

export type ListUserApiKeyPermissionsResponseListMetadata = {
  before?: string;
  after?: string;
};

export interface ListUserApiKeyPermissionsResponse {
  data: ListUserApiKeyPermission[];
  list_metadata: ListUserApiKeyPermissionsResponseListMetadata;
}

export type DataInstallation = {
  object: "data_installation" | "connected_account";
  id: string;
  /** @nullable */
  user_id?: string | null;
  /** @nullable */
  organization_id?: string | null;
  scopes: string[];
  state: "connected" | "needs_reauthorization";
  created_at: string;
  updated_at: string;
} & {
  /** @nullable */
  userlandUserId?: string | null;
  /** @nullable */
  organizationId?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type DataIntegration = {
  object: "data_integration";
  id: string;
  name: string;
  /** @nullable */
  description?: string | null;
  slug: string;
  integrationType:
    | "asana"
    | "attio"
    | "box"
    | "cal-dot-com"
    | "calendly"
    | "confluence"
    | "datadog"
    | "dropbox"
    | "frame-io"
    | "front"
    | "github"
    | "gitlab"
    | "gmail"
    | "gong"
    | "google"
    | "google-calendar"
    | "google-drive"
    | "helpscout"
    | "hubspot"
    | "intercom"
    | "intralinks"
    | "jira"
    | "linear"
    | "microsoft"
    | "microsoft-onedrive"
    | "microsoft-onenote"
    | "microsoft-outlook"
    | "microsoft-outlook-calendar"
    | "microsoft-sharepoint"
    | "microsoft-teams"
    | "microsoft-todo"
    | "notion"
    | "prefect"
    | "pydantic-logfire"
    | "quickbooks"
    | "salesforce"
    | "sentry"
    | "slack"
    | "snowflake"
    | "stripe"
    | "xero"
    | "zendesk"
    | "zoom"
    | string;
  ownership: "userland_user" | "organization";
  credentialsType: "shared" | "custom";
  scopes?: string[] | null;
  createdAt: string;
  updatedAt: string;
  installation?: DataInstallation | null;
} & {
  iconSlug?: string;
  iconUrl?: string;
  iconDarkUrl?: string;
};

export interface DataIntegrationsResponse {
  data: DataIntegration[];
}

export type GetDataIntegrationAuthorizeUrlResponse = {
  url: string;
  redirectToken: string;
} & {
  handoffToken?: string;
};

export interface GetAuthorizationStatusResponse {
  isConnecting: boolean;
}

export type DirectoryType = (typeof DirectoryType)[keyof typeof DirectoryType];

export const DirectoryType = {
  azure_scim_v20: "azure scim v2.0",
  bamboohr: "bamboohr",
  breathe_hr: "breathe hr",
  cezanne_hr: "cezanne hr",
  cyberark_scim_v20: "cyberark scim v2.0",
  fourth_hr: "fourth hr",
  generic_scim_v20: "generic scim v2.0",
  gsuite_directory: "gsuite directory",
  gusto: "gusto",
  hibob: "hibob",
  jump_cloud_scim_v20: "jump cloud scim v2.0",
  okta_scim_v20: "okta scim v2.0",
  onelogin_scim_v20: "onelogin scim v2.0",
  people_hr: "people hr",
  personio: "personio",
  pingfederate_scim_v20: "pingfederate scim v2.0",
  rippling: "rippling",
  rippling_scim_v20: "rippling scim v2.0",
  s3: "s3",
  sailpoint_scim_v20: "sailpoint scim v2.0",
  sftp: "sftp",
  sftp_workday: "sftp workday",
  workday: "workday",
} as const;

export type DirectoryState =
  (typeof DirectoryState)[keyof typeof DirectoryState];

export const DirectoryState = {
  requires_type: "requires_type",
  linked: "linked",
  validating: "validating",
  invalid_credentials: "invalid_credentials",
  unlinked: "unlinked",
  deleting: "deleting",
} as const;

export interface DirectoryUsersMetadata {
  active: number;
  inactive: number;
}

export interface DirectoryMetadata {
  users: DirectoryUsersMetadata;
  groups: number;
}

export interface Directory {
  id: string;
  name: string;
  type: DirectoryType;
  state: DirectoryState;
  createdAt: string;
  updatedAt: string;
  /** @nullable */
  lastSyncRunAt?: string | null;
  metadata: DirectoryMetadata;
}

export interface DirectoriesResponse {
  data: Directory[];
  link: string;
}

export type CreateOrganizationApiKey400ErrorsItem = {
  code: string;
  field: string;
};

export type CreateOrganizationApiKey400 = {
  message: string;
  errors: CreateOrganizationApiKey400ErrorsItem[];
};

export type CreateOrganizationApiKey403 = {
  message: string;
};

export type CreateOrganizationApiKey404 = {
  message: string;
};

export type CreateOrganizationApiKey422ErrorsItem = {
  code: string;
  field: string;
};

export type CreateOrganizationApiKey422 = {
  message: string;
  errors: CreateOrganizationApiKey422ErrorsItem[];
};

export type ListOrganizationApiKeysParams = {
  limit?: number;
  before?: string;
  after?: string;
  search?: string;
};

export type ListOrganizationApiKeys400ErrorsItem = {
  code: string;
  field: string;
};

export type ListOrganizationApiKeys400 = {
  message: string;
  errors: ListOrganizationApiKeys400ErrorsItem[];
};

export type ListOrganizationApiKeys403 = {
  message: string;
};

export type ListOrganizationApiKeyPermissionsParams = {
  search?: string;
  limit?: number;
  before?: string;
  after?: string;
};

export type ListOrganizationApiKeyPermissions400ErrorsItem = {
  code: string;
  field: string;
};

export type ListOrganizationApiKeyPermissions400 = {
  message: string;
  errors: ListOrganizationApiKeyPermissions400ErrorsItem[];
};

export type ListOrganizationApiKeyPermissions403 = {
  message: string;
};

export type DeleteOrganizationApiKey200 = {
  success: boolean;
};

export type DeleteOrganizationApiKey403 = {
  message: string;
};

export type DeleteOrganizationApiKey404 = {
  message: string;
};

export type DeleteDataInstallation200 = {
  success: boolean;
};

export type DeleteDataInstallation403 = {
  message: string;
};

export type DeleteDataInstallation404 = {
  message: string;
};

export type MyDataIntegrations403 = {
  message: string;
};

export type GetDataInstallationAuthorizationStatus400 = {
  message: string;
};

export type GetDataInstallationAuthorizationStatus403 = {
  message: string;
};

export type GetDataInstallationAuthorizationStatus404 = {
  message: string;
};

export type GetDataIntegrationAuthorizeUrlParams = {
  requireHandoff?: boolean;
};

export type GetDataIntegrationAuthorizeUrl400 = {
  message: string;
};

export type GetDataIntegrationAuthorizeUrl403 = {
  message: string;
};

export type GetDataIntegrationAuthorizeUrl404 = {
  message: string;
};

export type CreateUserApiKey400ErrorsItem = {
  code: string;
  field: string;
};

export type CreateUserApiKey400 = {
  message: string;
  errors: CreateUserApiKey400ErrorsItem[];
};

export type CreateUserApiKey403 = {
  message: string;
};

export type CreateUserApiKey404 = {
  message: string;
};

export type CreateUserApiKey422ErrorsItem = {
  code: string;
  field: string;
};

export type CreateUserApiKey422 = {
  message: string;
  errors: CreateUserApiKey422ErrorsItem[];
};

export type ListUserApiKeysParams = {
  limit?: number;
  before?: string;
  after?: string;
  search?: string;
};

export type ListUserApiKeys400ErrorsItem = {
  code: string;
  field: string;
};

export type ListUserApiKeys400 = {
  message: string;
  errors: ListUserApiKeys400ErrorsItem[];
};

export type ListUserApiKeys403 = {
  message: string;
};

export type ListUserApiKeyPermissionsParams = {
  search?: string;
  limit?: number;
  before?: string;
  after?: string;
};

export type ListUserApiKeyPermissions400ErrorsItem = {
  code: string;
  field: string;
};

export type ListUserApiKeyPermissions400 = {
  message: string;
  errors: ListUserApiKeyPermissions400ErrorsItem[];
};

export type ListUserApiKeyPermissions403 = {
  message: string;
};

export type DeleteUserApiKey200 = {
  success: boolean;
};

export type DeleteUserApiKey403 = {
  message: string;
};

export type DeleteUserApiKey404 = {
  message: string;
};

export type InviteMemberInput = {
  email: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  roles: string[];
};

export type InviteMember400 = {
  message: string;
};

export type InviteMember403 = {
  message: string;
};

export type InviteMember404 = {
  message: string;
};

export type RevokeInvite400 = {
  message: string;
};

export type RevokeInvite403 = {
  message: string;
};

export type RevokeInvite404 = {
  message: string;
};

export type ResendInvite400 = {
  message: string;
};

export type ResendInvite403 = {
  message: string;
};

export type ResendInvite404 = {
  message: string;
};

export type ResendInvite422 = {
  message: string;
};

export type MembersParams = {
  search?: string;
  limit?: string;
  before?: string;
  after?: string;
  role?: string;
};

export type Members403 = {
  message: string;
};

export type Members404 = {
  message: string;
};

export type RemoveMember400 = {
  message: string;
};

export type RemoveMember403 = {
  message: string;
};

export type RemoveMember404 = {
  message: string;
};

export type UpdateMemberInput = {
  roles: string[];
};

export type UpdateMember400 = {
  message: string;
};

export type UpdateMember403 = {
  message: string;
};

export type UpdateMember404 = {
  message: string;
};

export type UpdateMember422 = {
  message: string;
};

export type Organizations403 = {
  message: string;
};

export type Organizations404 = {
  message: string;
};

export type Roles403 = {
  message: string;
};

export type Roles404 = {
  message: string;
};

export type RolesAndConfig403 = {
  message: string;
};

export type RolesAndConfig404 = {
  message: string;
};

export type AuthenticationInformation403 = {
  message: string;
};

export type CreatePassword201 = {
  success: boolean;
};

export type CreatePassword400 = {
  message: string;
};

export type CreatePassword403 = {
  message: string;
};

export type CreateTotpFactor400 = {
  message: string;
};

export type CreateTotpFactor403 = {
  message: string;
};

export type Me403 = {
  message: string;
};

export type UpdateMeInput = {
  firstName?: string;
  lastName?: string;
  locale?: string;
};

export type UpdateMe400 = {
  message: string;
};

export type UpdateMe403 = {
  message: string;
};

export type RegisterPasskey400 = {
  message: string;
};

export type RegisterPasskey403 = {
  message: string;
};

export type VerifyPasskey200 = {
  success: boolean;
};

export type VerifyPasskey400 = {
  message: string;
};

export type VerifyPasskey403 = {
  message: string;
};

export type DeletePasskey200 = {
  success: boolean;
};

export type DeletePasskey400 = {
  message: string;
};

export type DeletePasskey403 = {
  message: string;
};

export type SendEmailChange400 = {
  message: string;
};

export type SendEmailChange403 = {
  message: string;
};

export type SendEmailChange409 = {
  message: string;
};

export type SendEmailChange422 = {
  message: string;
};

export type SendEmailChange429 = {
  message: string;
};

export type SendVerification400 = {
  message: string;
};

export type SendVerification403 = {
  message: string;
};

export type Sessions403 = {
  message: string;
};

export type RevokeAllSessions200 = {
  success: boolean;
};

export type RevokeAllSessions400 = {
  message: string;
};

export type RevokeAllSessions403 = {
  message: string;
};

export type RevokeSession200 = {
  success: boolean;
};

export type RevokeSession400 = {
  message: string;
};

export type RevokeSession403 = {
  message: string;
};

export type DeleteTotpFactors200 = {
  success: boolean;
};

export type DeleteTotpFactors400 = {
  message: string;
};

export type DeleteTotpFactors403 = {
  message: string;
};

export type UpdatePassword201 = {
  success: boolean;
};

export type UpdatePassword400 = {
  message: string;
};

export type UpdatePassword403 = {
  message: string;
};

export type Verify400 = {
  message: string;
};

export type Verify403 = {
  message: string;
};

export type VerifyEmailChange400 = {
  message: string;
};

export type VerifyEmailChange403 = {
  message: string;
};

export type VerifyEmailChange409 = {
  message: string;
};

export type VerifyEmailChange422 = {
  message: string;
};

export type VerifyEmailChange429 = {
  message: string;
};

export type VerifyTotpFactor200 = {
  success: boolean;
};

export type VerifyTotpFactor400 = {
  message: string;
};

export type VerifyTotpFactor403 = {
  message: string;
};

export type GetAuditLogStream200 =
  | AuditLogStreamJSON
  | { [key: string]: unknown };

export type GetAuditLogStream403 = {
  message: string;
};

export type GenerateAdminPortalLinkParams = {
  intent: GenerateAdminPortalLinkIntent;
};

export type GenerateAdminPortalLinkIntent =
  (typeof GenerateAdminPortalLinkIntent)[keyof typeof GenerateAdminPortalLinkIntent];

export const GenerateAdminPortalLinkIntent = {
  domain_verification: "domain_verification",
  sso: "sso",
  log_streams: "log_streams",
} as const;

export type GenerateAdminPortalLink201 = {
  link: string;
};

export type GenerateAdminPortalLink403 = {
  message: string;
};

export type GenerateAdminPortalLink404 = {
  message: string;
};

export type GenerateAdminPortalLink500 = {
  message: string;
};

export type ListOrganizationDomains200 = {
  data: OrganizationDomain[];
};

export type ListOrganizationDomains403 = {
  message: string;
};

export type ListOrganizationDomains404 = {
  message: string;
};

export type DeleteOrganizationDomain403 = {
  message: string;
};

export type DeleteOrganizationDomain404 = {
  message: string;
};

export type ReverifyOrganizationDomain403 = {
  message: string;
};

export type ReverifyOrganizationDomain404 = {
  message: string;
};

export type ListDirectories403 = {
  message: string;
};

export type ListDirectories404 = {
  message: string;
};

export type GetDirectory403 = {
  message: string;
};

export type GetDirectory404 = {
  message: string;
};

export type Settings403 = {
  message: string;
};

export const useCreateOrganizationApiKeyHook = () => {
  const createOrganizationApiKey =
    useWidgetsApiClient<CreateOrganizationApiKeyResponse>();

  return useCallback(
    (
      createOrganizationApiKeyRequest: BodyType<CreateOrganizationApiKeyRequest>,
      signal?: AbortSignal,
    ) => {
      return createOrganizationApiKey({
        url: `/_widgets/ApiKeys/organization-api-keys`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: createOrganizationApiKeyRequest,
        signal,
      });
    },
    [createOrganizationApiKey],
  );
};

export const useCreateOrganizationApiKeyMutationOptions = <
  TError = ErrorType<
    | CreateOrganizationApiKey400
    | CreateOrganizationApiKey403
    | CreateOrganizationApiKey404
    | CreateOrganizationApiKey422
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateOrganizationApiKeyHook>>>,
    TError,
    { data: BodyType<CreateOrganizationApiKeyRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationApiKeyHook>>>,
  TError,
  { data: BodyType<CreateOrganizationApiKeyRequest> },
  TContext
> => {
  const mutationKey = ["createOrganizationApiKey"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createOrganizationApiKey = useCreateOrganizationApiKeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateOrganizationApiKeyHook>>>,
    { data: BodyType<CreateOrganizationApiKeyRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createOrganizationApiKey(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrganizationApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationApiKeyHook>>>
>;
export type CreateOrganizationApiKeyMutationBody =
  BodyType<CreateOrganizationApiKeyRequest>;
export type CreateOrganizationApiKeyMutationError = ErrorType<
  | CreateOrganizationApiKey400
  | CreateOrganizationApiKey403
  | CreateOrganizationApiKey404
  | CreateOrganizationApiKey422
>;

export const useCreateOrganizationApiKey = <
  TError = ErrorType<
    | CreateOrganizationApiKey400
    | CreateOrganizationApiKey403
    | CreateOrganizationApiKey404
    | CreateOrganizationApiKey422
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateOrganizationApiKeyHook>>>,
      TError,
      { data: BodyType<CreateOrganizationApiKeyRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationApiKeyHook>>>,
  TError,
  { data: BodyType<CreateOrganizationApiKeyRequest> },
  TContext
> => {
  return useMutation(
    useCreateOrganizationApiKeyMutationOptions(options),
    queryClient,
  );
};

export const useListOrganizationApiKeysHook = () => {
  const listOrganizationApiKeys =
    useWidgetsApiClient<ListOrganizationApiKeysResponse>();

  return useCallback(
    (params?: ListOrganizationApiKeysParams, signal?: AbortSignal) => {
      return listOrganizationApiKeys({
        url: `/_widgets/ApiKeys/organization-api-keys`,
        method: "GET",
        params,
        signal,
      });
    },
    [listOrganizationApiKeys],
  );
};

export const getListOrganizationApiKeysQueryKey = (
  params?: ListOrganizationApiKeysParams,
) => {
  return [
    `/_widgets/ApiKeys/organization-api-keys`,
    ...(params ? [params] : []),
  ] as const;
};

export const useListOrganizationApiKeysQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>
  >,
  TError = ErrorType<ListOrganizationApiKeys400 | ListOrganizationApiKeys403>,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOrganizationApiKeysQueryKey(params);

  const listOrganizationApiKeys = useListOrganizationApiKeysHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>
  > = ({ signal }) => listOrganizationApiKeys(params, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOrganizationApiKeysQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>
>;
export type ListOrganizationApiKeysQueryError = ErrorType<
  ListOrganizationApiKeys400 | ListOrganizationApiKeys403
>;

export function useListOrganizationApiKeys<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>
  >,
  TError = ErrorType<ListOrganizationApiKeys400 | ListOrganizationApiKeys403>,
>(
  params: undefined | ListOrganizationApiKeysParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeys<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>
  >,
  TError = ErrorType<ListOrganizationApiKeys400 | ListOrganizationApiKeys403>,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeys<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>
  >,
  TError = ErrorType<ListOrganizationApiKeys400 | ListOrganizationApiKeys403>,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListOrganizationApiKeys<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>
  >,
  TError = ErrorType<ListOrganizationApiKeys400 | ListOrganizationApiKeys403>,
>(
  params?: ListOrganizationApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationApiKeysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useListOrganizationApiKeysQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useListOrganizationApiKeyPermissionsHook = () => {
  const listOrganizationApiKeyPermissions =
    useWidgetsApiClient<ListOrganizationApiKeyPermissionsResponse>();

  return useCallback(
    (
      params?: ListOrganizationApiKeyPermissionsParams,
      signal?: AbortSignal,
    ) => {
      return listOrganizationApiKeyPermissions({
        url: `/_widgets/ApiKeys/permissions`,
        method: "GET",
        params,
        signal,
      });
    },
    [listOrganizationApiKeyPermissions],
  );
};

export const getListOrganizationApiKeyPermissionsQueryKey = (
  params?: ListOrganizationApiKeyPermissionsParams,
) => {
  return [
    `/_widgets/ApiKeys/permissions`,
    ...(params ? [params] : []),
  ] as const;
};

export const useListOrganizationApiKeyPermissionsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListOrganizationApiKeyPermissions400 | ListOrganizationApiKeyPermissions403
  >,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListOrganizationApiKeyPermissionsQueryKey(params);

  const listOrganizationApiKeyPermissions =
    useListOrganizationApiKeyPermissionsHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
    >
  > = ({ signal }) => listOrganizationApiKeyPermissions(params, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOrganizationApiKeyPermissionsQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
  >
>;
export type ListOrganizationApiKeyPermissionsQueryError = ErrorType<
  ListOrganizationApiKeyPermissions400 | ListOrganizationApiKeyPermissions403
>;

export function useListOrganizationApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListOrganizationApiKeyPermissions400 | ListOrganizationApiKeyPermissions403
  >,
>(
  params: undefined | ListOrganizationApiKeyPermissionsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
            >
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListOrganizationApiKeyPermissions400 | ListOrganizationApiKeyPermissions403
  >,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
            >
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListOrganizationApiKeyPermissions400 | ListOrganizationApiKeyPermissions403
  >,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListOrganizationApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListOrganizationApiKeyPermissions400 | ListOrganizationApiKeyPermissions403
  >,
>(
  params?: ListOrganizationApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListOrganizationApiKeyPermissionsHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useListOrganizationApiKeyPermissionsQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useDeleteOrganizationApiKeyHook = () => {
  const deleteOrganizationApiKey =
    useWidgetsApiClient<DeleteOrganizationApiKey200>();

  return useCallback(
    (apiKeyId: string, signal?: AbortSignal) => {
      return deleteOrganizationApiKey({
        url: `/_widgets/ApiKeys/${apiKeyId}`,
        method: "DELETE",
        signal,
      });
    },
    [deleteOrganizationApiKey],
  );
};

export const useDeleteOrganizationApiKeyMutationOptions = <
  TError = ErrorType<DeleteOrganizationApiKey403 | DeleteOrganizationApiKey404>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationApiKeyHook>>>,
    TError,
    { apiKeyId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationApiKeyHook>>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  const mutationKey = ["deleteOrganizationApiKey"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteOrganizationApiKey = useDeleteOrganizationApiKeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationApiKeyHook>>>,
    { apiKeyId: string }
  > = (props) => {
    const { apiKeyId } = props ?? {};

    return deleteOrganizationApiKey(apiKeyId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganizationApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationApiKeyHook>>>
>;

export type DeleteOrganizationApiKeyMutationError = ErrorType<
  DeleteOrganizationApiKey403 | DeleteOrganizationApiKey404
>;

export const useDeleteOrganizationApiKey = <
  TError = ErrorType<DeleteOrganizationApiKey403 | DeleteOrganizationApiKey404>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationApiKeyHook>>>,
      TError,
      { apiKeyId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationApiKeyHook>>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  return useMutation(
    useDeleteOrganizationApiKeyMutationOptions(options),
    queryClient,
  );
};

export const useDeleteDataInstallationHook = () => {
  const deleteDataInstallation =
    useWidgetsApiClient<DeleteDataInstallation200>();

  return useCallback(
    (installationId: string, signal?: AbortSignal) => {
      return deleteDataInstallation({
        url: `/_widgets/DataIntegrations/installations/${installationId}`,
        method: "DELETE",
        signal,
      });
    },
    [deleteDataInstallation],
  );
};

export const useDeleteDataInstallationMutationOptions = <
  TError = ErrorType<DeleteDataInstallation403 | DeleteDataInstallation404>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteDataInstallationHook>>>,
    TError,
    { installationId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteDataInstallationHook>>>,
  TError,
  { installationId: string },
  TContext
> => {
  const mutationKey = ["deleteDataInstallation"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteDataInstallation = useDeleteDataInstallationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteDataInstallationHook>>>,
    { installationId: string }
  > = (props) => {
    const { installationId } = props ?? {};

    return deleteDataInstallation(installationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteDataInstallationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteDataInstallationHook>>>
>;

export type DeleteDataInstallationMutationError = ErrorType<
  DeleteDataInstallation403 | DeleteDataInstallation404
>;

export const useDeleteDataInstallation = <
  TError = ErrorType<DeleteDataInstallation403 | DeleteDataInstallation404>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteDataInstallationHook>>>,
      TError,
      { installationId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteDataInstallationHook>>>,
  TError,
  { installationId: string },
  TContext
> => {
  return useMutation(
    useDeleteDataInstallationMutationOptions(options),
    queryClient,
  );
};

export const useMyDataIntegrationsHook = () => {
  const myDataIntegrations = useWidgetsApiClient<DataIntegrationsResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return myDataIntegrations({
        url: `/_widgets/DataIntegrations/mine`,
        method: "GET",
        signal,
      });
    },
    [myDataIntegrations],
  );
};

export const getMyDataIntegrationsQueryKey = () => {
  return [`/_widgets/DataIntegrations/mine`] as const;
};

export const useMyDataIntegrationsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
  TError = ErrorType<MyDataIntegrations403>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMyDataIntegrationsQueryKey();

  const myDataIntegrations = useMyDataIntegrationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>
  > = ({ signal }) => myDataIntegrations(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MyDataIntegrationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>
>;
export type MyDataIntegrationsQueryError = ErrorType<MyDataIntegrations403>;

export function useMyDataIntegrations<
  TData = Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
  TError = ErrorType<MyDataIntegrations403>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMyDataIntegrations<
  TData = Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
  TError = ErrorType<MyDataIntegrations403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMyDataIntegrations<
  TData = Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
  TError = ErrorType<MyDataIntegrations403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useMyDataIntegrations<
  TData = Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
  TError = ErrorType<MyDataIntegrations403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMyDataIntegrationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useMyDataIntegrationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetDataInstallationAuthorizationStatusHook = () => {
  const getDataInstallationAuthorizationStatus =
    useWidgetsApiClient<GetAuthorizationStatusResponse>();

  return useCallback(
    (dataIntegrationId: string, state: string, signal?: AbortSignal) => {
      return getDataInstallationAuthorizationStatus({
        url: `/_widgets/DataIntegrations/${dataIntegrationId}/authorization-status/${state}`,
        method: "GET",
        signal,
      });
    },
    [getDataInstallationAuthorizationStatus],
  );
};

export const getGetDataInstallationAuthorizationStatusQueryKey = (
  dataIntegrationId: string,
  state: string,
) => {
  return [
    `/_widgets/DataIntegrations/${dataIntegrationId}/authorization-status/${state}`,
  ] as const;
};

export const useGetDataInstallationAuthorizationStatusQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>>
  >,
  TError = ErrorType<
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404
  >,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDataInstallationAuthorizationStatusQueryKey(dataIntegrationId, state);

  const getDataInstallationAuthorizationStatus =
    useGetDataInstallationAuthorizationStatusHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
      >
    >
  > = ({ signal }) =>
    getDataInstallationAuthorizationStatus(dataIntegrationId, state, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
      >
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDataInstallationAuthorizationStatusQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>>
  >
>;
export type GetDataInstallationAuthorizationStatusQueryError = ErrorType<
  | GetDataInstallationAuthorizationStatus400
  | GetDataInstallationAuthorizationStatus403
  | GetDataInstallationAuthorizationStatus404
>;

export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>>
  >,
  TError = ErrorType<
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404
  >,
>(
  dataIntegrationId: string,
  state: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
            >
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>>
  >,
  TError = ErrorType<
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404
  >,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
            >
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>>
  >,
  TError = ErrorType<
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404
  >,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetDataInstallationAuthorizationStatus<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>>
  >,
  TError = ErrorType<
    | GetDataInstallationAuthorizationStatus400
    | GetDataInstallationAuthorizationStatus403
    | GetDataInstallationAuthorizationStatus404
  >,
>(
  dataIntegrationId: string,
  state: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useGetDataInstallationAuthorizationStatusHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetDataInstallationAuthorizationStatusQueryOptions(
    dataIntegrationId,
    state,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetDataIntegrationAuthorizeUrlHook = () => {
  const getDataIntegrationAuthorizeUrl =
    useWidgetsApiClient<GetDataIntegrationAuthorizeUrlResponse>();

  return useCallback(
    (
      slug: string,
      params?: GetDataIntegrationAuthorizeUrlParams,
      signal?: AbortSignal,
    ) => {
      return getDataIntegrationAuthorizeUrl({
        url: `/_widgets/DataIntegrations/${slug}/authorize`,
        method: "GET",
        params,
        signal,
      });
    },
    [getDataIntegrationAuthorizeUrl],
  );
};

export const getGetDataIntegrationAuthorizeUrlQueryKey = (
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
) => {
  return [
    `/_widgets/DataIntegrations/${slug}/authorize`,
    ...(params ? [params] : []),
  ] as const;
};

export const useGetDataIntegrationAuthorizeUrlQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
  >,
  TError = ErrorType<
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404
  >,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetDataIntegrationAuthorizeUrlQueryKey(slug, params);

  const getDataIntegrationAuthorizeUrl =
    useGetDataIntegrationAuthorizeUrlHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
    >
  > = ({ signal }) => getDataIntegrationAuthorizeUrl(slug, params, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDataIntegrationAuthorizeUrlQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>>
>;
export type GetDataIntegrationAuthorizeUrlQueryError = ErrorType<
  | GetDataIntegrationAuthorizeUrl400
  | GetDataIntegrationAuthorizeUrl403
  | GetDataIntegrationAuthorizeUrl404
>;

export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
  >,
  TError = ErrorType<
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404
  >,
>(
  slug: string,
  params: undefined | GetDataIntegrationAuthorizeUrlParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
  >,
  TError = ErrorType<
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404
  >,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
  >,
  TError = ErrorType<
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404
  >,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetDataIntegrationAuthorizeUrl<
  TData = Awaited<
    ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
  >,
  TError = ErrorType<
    | GetDataIntegrationAuthorizeUrl400
    | GetDataIntegrationAuthorizeUrl403
    | GetDataIntegrationAuthorizeUrl404
  >,
>(
  slug: string,
  params?: GetDataIntegrationAuthorizeUrlParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useGetDataIntegrationAuthorizeUrlHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetDataIntegrationAuthorizeUrlQueryOptions(
    slug,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useCreateUserApiKeyHook = () => {
  const createUserApiKey = useWidgetsApiClient<CreateUserApiKeyResponse>();

  return useCallback(
    (
      createUserApiKeyRequest: BodyType<CreateUserApiKeyRequest>,
      signal?: AbortSignal,
    ) => {
      return createUserApiKey({
        url: `/_widgets/UserApiKeys/api-keys`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: createUserApiKeyRequest,
        signal,
      });
    },
    [createUserApiKey],
  );
};

export const useCreateUserApiKeyMutationOptions = <
  TError = ErrorType<
    | CreateUserApiKey400
    | CreateUserApiKey403
    | CreateUserApiKey404
    | CreateUserApiKey422
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateUserApiKeyHook>>>,
    TError,
    { data: BodyType<CreateUserApiKeyRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateUserApiKeyHook>>>,
  TError,
  { data: BodyType<CreateUserApiKeyRequest> },
  TContext
> => {
  const mutationKey = ["createUserApiKey"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createUserApiKey = useCreateUserApiKeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateUserApiKeyHook>>>,
    { data: BodyType<CreateUserApiKeyRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createUserApiKey(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateUserApiKeyHook>>>
>;
export type CreateUserApiKeyMutationBody = BodyType<CreateUserApiKeyRequest>;
export type CreateUserApiKeyMutationError = ErrorType<
  | CreateUserApiKey400
  | CreateUserApiKey403
  | CreateUserApiKey404
  | CreateUserApiKey422
>;

export const useCreateUserApiKey = <
  TError = ErrorType<
    | CreateUserApiKey400
    | CreateUserApiKey403
    | CreateUserApiKey404
    | CreateUserApiKey422
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateUserApiKeyHook>>>,
      TError,
      { data: BodyType<CreateUserApiKeyRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateUserApiKeyHook>>>,
  TError,
  { data: BodyType<CreateUserApiKeyRequest> },
  TContext
> => {
  return useMutation(useCreateUserApiKeyMutationOptions(options), queryClient);
};

export const useListUserApiKeysHook = () => {
  const listUserApiKeys = useWidgetsApiClient<ListUserApiKeysResponse>();

  return useCallback(
    (params?: ListUserApiKeysParams, signal?: AbortSignal) => {
      return listUserApiKeys({
        url: `/_widgets/UserApiKeys/api-keys`,
        method: "GET",
        params,
        signal,
      });
    },
    [listUserApiKeys],
  );
};

export const getListUserApiKeysQueryKey = (params?: ListUserApiKeysParams) => {
  return [
    `/_widgets/UserApiKeys/api-keys`,
    ...(params ? [params] : []),
  ] as const;
};

export const useListUserApiKeysQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
  TError = ErrorType<ListUserApiKeys400 | ListUserApiKeys403>,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListUserApiKeysQueryKey(params);

  const listUserApiKeys = useListUserApiKeysHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>
  > = ({ signal }) => listUserApiKeys(params, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUserApiKeysQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>
>;
export type ListUserApiKeysQueryError = ErrorType<
  ListUserApiKeys400 | ListUserApiKeys403
>;

export function useListUserApiKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
  TError = ErrorType<ListUserApiKeys400 | ListUserApiKeys403>,
>(
  params: undefined | ListUserApiKeysParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
  TError = ErrorType<ListUserApiKeys400 | ListUserApiKeys403>,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
  TError = ErrorType<ListUserApiKeys400 | ListUserApiKeys403>,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListUserApiKeys<
  TData = Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
  TError = ErrorType<ListUserApiKeys400 | ListUserApiKeys403>,
>(
  params?: ListUserApiKeysParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserApiKeysHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useListUserApiKeysQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useListUserApiKeyPermissionsHook = () => {
  const listUserApiKeyPermissions =
    useWidgetsApiClient<ListUserApiKeyPermissionsResponse>();

  return useCallback(
    (params?: ListUserApiKeyPermissionsParams, signal?: AbortSignal) => {
      return listUserApiKeyPermissions({
        url: `/_widgets/UserApiKeys/permissions`,
        method: "GET",
        params,
        signal,
      });
    },
    [listUserApiKeyPermissions],
  );
};

export const getListUserApiKeyPermissionsQueryKey = (
  params?: ListUserApiKeyPermissionsParams,
) => {
  return [
    `/_widgets/UserApiKeys/permissions`,
    ...(params ? [params] : []),
  ] as const;
};

export const useListUserApiKeyPermissionsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403
  >,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListUserApiKeyPermissionsQueryKey(params);

  const listUserApiKeyPermissions = useListUserApiKeyPermissionsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>>
  > = ({ signal }) => listUserApiKeyPermissions(params, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUserApiKeyPermissionsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>>
>;
export type ListUserApiKeyPermissionsQueryError = ErrorType<
  ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403
>;

export function useListUserApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403
  >,
>(
  params: undefined | ListUserApiKeyPermissionsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403
  >,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListUserApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403
  >,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListUserApiKeyPermissions<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
  >,
  TError = ErrorType<
    ListUserApiKeyPermissions400 | ListUserApiKeyPermissions403
  >,
>(
  params?: ListUserApiKeyPermissionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserApiKeyPermissionsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useListUserApiKeyPermissionsQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useDeleteUserApiKeyHook = () => {
  const deleteUserApiKey = useWidgetsApiClient<DeleteUserApiKey200>();

  return useCallback(
    (apiKeyId: string, signal?: AbortSignal) => {
      return deleteUserApiKey({
        url: `/_widgets/UserApiKeys/${apiKeyId}`,
        method: "DELETE",
        signal,
      });
    },
    [deleteUserApiKey],
  );
};

export const useDeleteUserApiKeyMutationOptions = <
  TError = ErrorType<DeleteUserApiKey403 | DeleteUserApiKey404>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserApiKeyHook>>>,
    TError,
    { apiKeyId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserApiKeyHook>>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  const mutationKey = ["deleteUserApiKey"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteUserApiKey = useDeleteUserApiKeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserApiKeyHook>>>,
    { apiKeyId: string }
  > = (props) => {
    const { apiKeyId } = props ?? {};

    return deleteUserApiKey(apiKeyId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUserApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserApiKeyHook>>>
>;

export type DeleteUserApiKeyMutationError = ErrorType<
  DeleteUserApiKey403 | DeleteUserApiKey404
>;

export const useDeleteUserApiKey = <
  TError = ErrorType<DeleteUserApiKey403 | DeleteUserApiKey404>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteUserApiKeyHook>>>,
      TError,
      { apiKeyId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserApiKeyHook>>>,
  TError,
  { apiKeyId: string },
  TContext
> => {
  return useMutation(useDeleteUserApiKeyMutationOptions(options), queryClient);
};

/**
 * Sends an invitation email to a user to join the organization. If the user does not have an account, they will be prompted to create one upon accepting.
 */
export const useInviteMemberHook = () => {
  const inviteMember = useWidgetsApiClient<InviteMemberResponse>();

  return useCallback(
    (inviteMemberInput: BodyType<InviteMemberInput>, signal?: AbortSignal) => {
      return inviteMember({
        url: `/_widgets/UserManagement/invite-user`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: inviteMemberInput,
        signal,
      });
    },
    [inviteMember],
  );
};

export const useInviteMemberMutationOptions = <
  TError = ErrorType<InviteMember400 | InviteMember403 | InviteMember404>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInviteMemberHook>>>,
    TError,
    { data: BodyType<InviteMemberInput> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useInviteMemberHook>>>,
  TError,
  { data: BodyType<InviteMemberInput> },
  TContext
> => {
  const mutationKey = ["inviteMember"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const inviteMember = useInviteMemberHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useInviteMemberHook>>>,
    { data: BodyType<InviteMemberInput> }
  > = (props) => {
    const { data } = props ?? {};

    return inviteMember(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteMemberMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useInviteMemberHook>>>
>;
export type InviteMemberMutationBody = BodyType<InviteMemberInput>;
export type InviteMemberMutationError = ErrorType<
  InviteMember400 | InviteMember403 | InviteMember404
>;

export const useInviteMember = <
  TError = ErrorType<InviteMember400 | InviteMember403 | InviteMember404>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useInviteMemberHook>>>,
      TError,
      { data: BodyType<InviteMemberInput> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useInviteMemberHook>>>,
  TError,
  { data: BodyType<InviteMemberInput> },
  TContext
> => {
  return useMutation(useInviteMemberMutationOptions(options), queryClient);
};

/**
 * Cancels a pending invitation for the specified user, preventing them from joining the organization via that invite link.
 */
export const useRevokeInviteHook = () => {
  const revokeInvite = useWidgetsApiClient<RevokeMemberResponse>();

  return useCallback(
    (userId: string, signal?: AbortSignal) => {
      return revokeInvite({
        url: `/_widgets/UserManagement/invites/${userId}`,
        method: "DELETE",
        signal,
      });
    },
    [revokeInvite],
  );
};

export const useRevokeInviteMutationOptions = <
  TError = ErrorType<RevokeInvite400 | RevokeInvite403 | RevokeInvite404>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRevokeInviteHook>>>,
    TError,
    { userId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRevokeInviteHook>>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ["revokeInvite"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const revokeInvite = useRevokeInviteHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRevokeInviteHook>>>,
    { userId: string }
  > = (props) => {
    const { userId } = props ?? {};

    return revokeInvite(userId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeInviteMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRevokeInviteHook>>>
>;

export type RevokeInviteMutationError = ErrorType<
  RevokeInvite400 | RevokeInvite403 | RevokeInvite404
>;

export const useRevokeInvite = <
  TError = ErrorType<RevokeInvite400 | RevokeInvite403 | RevokeInvite404>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRevokeInviteHook>>>,
      TError,
      { userId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRevokeInviteHook>>>,
  TError,
  { userId: string },
  TContext
> => {
  return useMutation(useRevokeInviteMutationOptions(options), queryClient);
};

/**
 * Resends the pending invitation email to the specified user. Returns an error if the invitation has already been accepted or has expired.
 */
export const useResendInviteHook = () => {
  const resendInvite = useWidgetsApiClient<ResendInviteResponse>();

  return useCallback(
    (userId: string, signal?: AbortSignal) => {
      return resendInvite({
        url: `/_widgets/UserManagement/invites/${userId}/resend`,
        method: "POST",
        signal,
      });
    },
    [resendInvite],
  );
};

export const useResendInviteMutationOptions = <
  TError = ErrorType<
    ResendInvite400 | ResendInvite403 | ResendInvite404 | ResendInvite422
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useResendInviteHook>>>,
    TError,
    { userId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useResendInviteHook>>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ["resendInvite"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const resendInvite = useResendInviteHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useResendInviteHook>>>,
    { userId: string }
  > = (props) => {
    const { userId } = props ?? {};

    return resendInvite(userId);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResendInviteMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useResendInviteHook>>>
>;

export type ResendInviteMutationError = ErrorType<
  ResendInvite400 | ResendInvite403 | ResendInvite404 | ResendInvite422
>;

export const useResendInvite = <
  TError = ErrorType<
    ResendInvite400 | ResendInvite403 | ResendInvite404 | ResendInvite422
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useResendInviteHook>>>,
      TError,
      { userId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useResendInviteHook>>>,
  TError,
  { userId: string },
  TContext
> => {
  return useMutation(useResendInviteMutationOptions(options), queryClient);
};

/**
 * Returns a paginated list of members belonging to the organization. Supports filtering by search term and role, and cursor-based pagination via before/after parameters.
 */
export const useMembersHook = () => {
  const members = useWidgetsApiClient<MembersResponse>();

  return useCallback(
    (params?: MembersParams, signal?: AbortSignal) => {
      return members({
        url: `/_widgets/UserManagement/members`,
        method: "GET",
        params,
        signal,
      });
    },
    [members],
  );
};

export const getMembersQueryKey = (params?: MembersParams) => {
  return [
    `/_widgets/UserManagement/members`,
    ...(params ? [params] : []),
  ] as const;
};

export const useMembersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
  TError = ErrorType<Members403 | Members404>,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMembersQueryKey(params);

  const members = useMembersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useMembersHook>>>
  > = ({ signal }) => members(params, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MembersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useMembersHook>>>
>;
export type MembersQueryError = ErrorType<Members403 | Members404>;

export function useMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
  TError = ErrorType<Members403 | Members404>,
>(
  params: undefined | MembersParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useMembersHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
  TError = ErrorType<Members403 | Members404>,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useMembersHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
  TError = ErrorType<Members403 | Members404>,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useMembers<
  TData = Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
  TError = ErrorType<Members403 | Members404>,
>(
  params?: MembersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMembersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useMembersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Removes the specified user from the organization by revoking their membership. The user account itself is not deleted.
 */
export const useRemoveMemberHook = () => {
  const removeMember = useWidgetsApiClient<RemoveMemberResponse>();

  return useCallback(
    (userId: string, signal?: AbortSignal) => {
      return removeMember({
        url: `/_widgets/UserManagement/members/${userId}`,
        method: "DELETE",
        signal,
      });
    },
    [removeMember],
  );
};

export const useRemoveMemberMutationOptions = <
  TError = ErrorType<RemoveMember400 | RemoveMember403 | RemoveMember404>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRemoveMemberHook>>>,
    TError,
    { userId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRemoveMemberHook>>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ["removeMember"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const removeMember = useRemoveMemberHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRemoveMemberHook>>>,
    { userId: string }
  > = (props) => {
    const { userId } = props ?? {};

    return removeMember(userId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveMemberMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRemoveMemberHook>>>
>;

export type RemoveMemberMutationError = ErrorType<
  RemoveMember400 | RemoveMember403 | RemoveMember404
>;

export const useRemoveMember = <
  TError = ErrorType<RemoveMember400 | RemoveMember403 | RemoveMember404>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRemoveMemberHook>>>,
      TError,
      { userId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRemoveMemberHook>>>,
  TError,
  { userId: string },
  TContext
> => {
  return useMutation(useRemoveMemberMutationOptions(options), queryClient);
};

/**
 * Updates the specified member's organization membership, such as changing their assigned role.
 */
export const useUpdateMemberHook = () => {
  const updateMember = useWidgetsApiClient<UpdateMemberResponse>();

  return useCallback(
    (
      userId: string,
      updateMemberInput: BodyType<UpdateMemberInput>,
      signal?: AbortSignal,
    ) => {
      return updateMember({
        url: `/_widgets/UserManagement/members/${userId}`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: updateMemberInput,
        signal,
      });
    },
    [updateMember],
  );
};

export const useUpdateMemberMutationOptions = <
  TError = ErrorType<
    UpdateMember400 | UpdateMember403 | UpdateMember404 | UpdateMember422
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateMemberHook>>>,
    TError,
    { userId: string; data: BodyType<UpdateMemberInput> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateMemberHook>>>,
  TError,
  { userId: string; data: BodyType<UpdateMemberInput> },
  TContext
> => {
  const mutationKey = ["updateMember"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateMember = useUpdateMemberHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateMemberHook>>>,
    { userId: string; data: BodyType<UpdateMemberInput> }
  > = (props) => {
    const { userId, data } = props ?? {};

    return updateMember(userId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMemberMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateMemberHook>>>
>;
export type UpdateMemberMutationBody = BodyType<UpdateMemberInput>;
export type UpdateMemberMutationError = ErrorType<
  UpdateMember400 | UpdateMember403 | UpdateMember404 | UpdateMember422
>;

export const useUpdateMember = <
  TError = ErrorType<
    UpdateMember400 | UpdateMember403 | UpdateMember404 | UpdateMember422
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateMemberHook>>>,
      TError,
      { userId: string; data: BodyType<UpdateMemberInput> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateMemberHook>>>,
  TError,
  { userId: string; data: BodyType<UpdateMemberInput> },
  TContext
> => {
  return useMutation(useUpdateMemberMutationOptions(options), queryClient);
};

/**
 * Returns the list of organizations the authenticated user is a member of.
 */
export const useOrganizationsHook = () => {
  const organizations = useWidgetsApiClient<OrganizationsResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return organizations({
        url: `/_widgets/UserManagement/organizations`,
        method: "GET",
        signal,
      });
    },
    [organizations],
  );
};

export const getOrganizationsQueryKey = () => {
  return [`/_widgets/UserManagement/organizations`] as const;
};

export const useOrganizationsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
  TError = ErrorType<Organizations403 | Organizations404>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getOrganizationsQueryKey();

  const organizations = useOrganizationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>
  > = ({ signal }) => organizations(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type OrganizationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>
>;
export type OrganizationsQueryError = ErrorType<
  Organizations403 | Organizations404
>;

export function useOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
  TError = ErrorType<Organizations403 | Organizations404>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
  TError = ErrorType<Organizations403 | Organizations404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
  TError = ErrorType<Organizations403 | Organizations404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
  TError = ErrorType<Organizations403 | Organizations404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useOrganizationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the list of roles available in the organization that can be assigned to members.
 */
export const useRolesHook = () => {
  const roles = useWidgetsApiClient<MemberRole[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return roles({
        url: `/_widgets/UserManagement/roles`,
        method: "GET",
        signal,
      });
    },
    [roles],
  );
};

export const getRolesQueryKey = () => {
  return [`/_widgets/UserManagement/roles`] as const;
};

export const useRolesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
  TError = ErrorType<Roles403 | Roles404>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRolesQueryKey();

  const roles = useRolesHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useRolesHook>>>
  > = ({ signal }) => roles(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RolesQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRolesHook>>>
>;
export type RolesQueryError = ErrorType<Roles403 | Roles404>;

export function useRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
  TError = ErrorType<Roles403 | Roles404>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useRolesHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
  TError = ErrorType<Roles403 | Roles404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useRolesHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
  TError = ErrorType<Roles403 | Roles404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
  TError = ErrorType<Roles403 | Roles404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useRolesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the list of roles available in the organization along with the user management configuration, such as whether role assignment is enabled.
 */
export const useRolesAndConfigHook = () => {
  const rolesAndConfig = useWidgetsApiClient<RolesAndConfigResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return rolesAndConfig({
        url: `/_widgets/UserManagement/roles-and-config`,
        method: "GET",
        signal,
      });
    },
    [rolesAndConfig],
  );
};

export const getRolesAndConfigQueryKey = () => {
  return [`/_widgets/UserManagement/roles-and-config`] as const;
};

export const useRolesAndConfigQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
  TError = ErrorType<RolesAndConfig403 | RolesAndConfig404>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRolesAndConfigQueryKey();

  const rolesAndConfig = useRolesAndConfigHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>
  > = ({ signal }) => rolesAndConfig(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RolesAndConfigQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>
>;
export type RolesAndConfigQueryError = ErrorType<
  RolesAndConfig403 | RolesAndConfig404
>;

export function useRolesAndConfig<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
  TError = ErrorType<RolesAndConfig403 | RolesAndConfig404>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRolesAndConfig<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
  TError = ErrorType<RolesAndConfig403 | RolesAndConfig404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useRolesAndConfig<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
  TError = ErrorType<RolesAndConfig403 | RolesAndConfig404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useRolesAndConfig<
  TData = Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
  TError = ErrorType<RolesAndConfig403 | RolesAndConfig404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useRolesAndConfigHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useRolesAndConfigQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the authentication methods and MFA factors configured for the authenticated user, including enrolled TOTP factors and passkeys.
 */
export const useAuthenticationInformationHook = () => {
  const authenticationInformation =
    useWidgetsApiClient<AuthenticationInformationResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return authenticationInformation({
        url: `/_widgets/UserProfile/authentication-information`,
        method: "GET",
        signal,
      });
    },
    [authenticationInformation],
  );
};

export const getAuthenticationInformationQueryKey = () => {
  return [`/_widgets/UserProfile/authentication-information`] as const;
};

export const useAuthenticationInformationQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
  >,
  TError = ErrorType<AuthenticationInformation403>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useAuthenticationInformationHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAuthenticationInformationQueryKey();

  const authenticationInformation = useAuthenticationInformationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useAuthenticationInformationHook>>>
  > = ({ signal }) => authenticationInformation(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useAuthenticationInformationHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AuthenticationInformationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useAuthenticationInformationHook>>>
>;
export type AuthenticationInformationQueryError =
  ErrorType<AuthenticationInformation403>;

export function useAuthenticationInformation<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
  >,
  TError = ErrorType<AuthenticationInformation403>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAuthenticationInformation<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
  >,
  TError = ErrorType<AuthenticationInformation403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAuthenticationInformation<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
  >,
  TError = ErrorType<AuthenticationInformation403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useAuthenticationInformation<
  TData = Awaited<
    ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
  >,
  TError = ErrorType<AuthenticationInformation403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useAuthenticationInformationHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useAuthenticationInformationQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Sets a password for the authenticated user. Only available when the user does not already have a password configured. Requires elevated access.
 */
export const useCreatePasswordHook = () => {
  const createPassword = useWidgetsApiClient<CreatePassword201>();

  return useCallback(
    (
      createPasswordRequest: BodyType<CreatePasswordRequest>,
      signal?: AbortSignal,
    ) => {
      return createPassword({
        url: `/_widgets/UserProfile/create-password`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: createPasswordRequest,
        signal,
      });
    },
    [createPassword],
  );
};

export const useCreatePasswordMutationOptions = <
  TError = ErrorType<CreatePassword400 | CreatePassword403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreatePasswordHook>>>,
    TError,
    { data: BodyType<CreatePasswordRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreatePasswordHook>>>,
  TError,
  { data: BodyType<CreatePasswordRequest> },
  TContext
> => {
  const mutationKey = ["createPassword"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createPassword = useCreatePasswordHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreatePasswordHook>>>,
    { data: BodyType<CreatePasswordRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createPassword(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePasswordMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreatePasswordHook>>>
>;
export type CreatePasswordMutationBody = BodyType<CreatePasswordRequest>;
export type CreatePasswordMutationError = ErrorType<
  CreatePassword400 | CreatePassword403
>;

export const useCreatePassword = <
  TError = ErrorType<CreatePassword400 | CreatePassword403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreatePasswordHook>>>,
      TError,
      { data: BodyType<CreatePasswordRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreatePasswordHook>>>,
  TError,
  { data: BodyType<CreatePasswordRequest> },
  TContext
> => {
  return useMutation(useCreatePasswordMutationOptions(options), queryClient);
};

/**
 * Initiates TOTP (authenticator app) enrollment for the authenticated user by generating a new TOTP secret and QR code. Requires elevated access.
 */
export const useCreateTotpFactorHook = () => {
  const createTotpFactor = useWidgetsApiClient<CreateTotpFactorResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return createTotpFactor({
        url: `/_widgets/UserProfile/create-totp-factor`,
        method: "POST",
        signal,
      });
    },
    [createTotpFactor],
  );
};

export const useCreateTotpFactorMutationOptions = <
  TError = ErrorType<CreateTotpFactor400 | CreateTotpFactor403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTotpFactorHook>>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateTotpFactorHook>>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["createTotpFactor"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createTotpFactor = useCreateTotpFactorHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateTotpFactorHook>>>,
    void
  > = () => {
    return createTotpFactor();
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTotpFactorMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateTotpFactorHook>>>
>;

export type CreateTotpFactorMutationError = ErrorType<
  CreateTotpFactor400 | CreateTotpFactor403
>;

export const useCreateTotpFactor = <
  TError = ErrorType<CreateTotpFactor400 | CreateTotpFactor403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateTotpFactorHook>>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateTotpFactorHook>>>,
  TError,
  void,
  TContext
> => {
  return useMutation(useCreateTotpFactorMutationOptions(options), queryClient);
};

/**
 * Returns the profile information of the currently authenticated user, including their name, email, and linked authentication factors.
 */
export const useMeHook = () => {
  const me = useWidgetsApiClient<Me>();

  return useCallback(
    (signal?: AbortSignal) => {
      return me({ url: `/_widgets/UserProfile/me`, method: "GET", signal });
    },
    [me],
  );
};

export const getMeQueryKey = () => {
  return [`/_widgets/UserProfile/me`] as const;
};

export const useMeQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
  TError = ErrorType<Me403>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMeQueryKey();

  const me = useMeHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useMeHook>>>
  > = ({ signal }) => me(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MeQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useMeHook>>>
>;
export type MeQueryError = ErrorType<Me403>;

export function useMe<
  TData = Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
  TError = ErrorType<Me403>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useMeHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMe<
  TData = Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
  TError = ErrorType<Me403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useMeHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useMe<
  TData = Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
  TError = ErrorType<Me403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useMe<
  TData = Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
  TError = ErrorType<Me403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useMeHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useMeQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates the profile information of the currently authenticated user, such as their first name, last name, or email address.
 */
export const useUpdateMeHook = () => {
  const updateMe = useWidgetsApiClient<Me>();

  return useCallback(
    (updateMeInput: BodyType<UpdateMeInput>, signal?: AbortSignal) => {
      return updateMe({
        url: `/_widgets/UserProfile/me`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: updateMeInput,
        signal,
      });
    },
    [updateMe],
  );
};

export const useUpdateMeMutationOptions = <
  TError = ErrorType<UpdateMe400 | UpdateMe403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
    TError,
    { data: BodyType<UpdateMeInput> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
  TError,
  { data: BodyType<UpdateMeInput> },
  TContext
> => {
  const mutationKey = ["updateMe"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateMe = useUpdateMeHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
    { data: BodyType<UpdateMeInput> }
  > = (props) => {
    const { data } = props ?? {};

    return updateMe(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMeMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>
>;
export type UpdateMeMutationBody = BodyType<UpdateMeInput>;
export type UpdateMeMutationError = ErrorType<UpdateMe400 | UpdateMe403>;

export const useUpdateMe = <
  TError = ErrorType<UpdateMe400 | UpdateMe403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
      TError,
      { data: BodyType<UpdateMeInput> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
  TError,
  { data: BodyType<UpdateMeInput> },
  TContext
> => {
  return useMutation(useUpdateMeMutationOptions(options), queryClient);
};

/**
 * Initiates passkey (WebAuthn) registration for the authenticated user by returning the credential creation options. Requires elevated access.
 */
export const useRegisterPasskeyHook = () => {
  const registerPasskey = useWidgetsApiClient<RegisterPasskeyResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return registerPasskey({
        url: `/_widgets/UserProfile/passkeys`,
        method: "POST",
        signal,
      });
    },
    [registerPasskey],
  );
};

export const useRegisterPasskeyMutationOptions = <
  TError = ErrorType<RegisterPasskey400 | RegisterPasskey403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRegisterPasskeyHook>>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRegisterPasskeyHook>>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["registerPasskey"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const registerPasskey = useRegisterPasskeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRegisterPasskeyHook>>>,
    void
  > = () => {
    return registerPasskey();
  };

  return { mutationFn, ...mutationOptions };
};

export type RegisterPasskeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRegisterPasskeyHook>>>
>;

export type RegisterPasskeyMutationError = ErrorType<
  RegisterPasskey400 | RegisterPasskey403
>;

export const useRegisterPasskey = <
  TError = ErrorType<RegisterPasskey400 | RegisterPasskey403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRegisterPasskeyHook>>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRegisterPasskeyHook>>>,
  TError,
  void,
  TContext
> => {
  return useMutation(useRegisterPasskeyMutationOptions(options), queryClient);
};

/**
 * Completes passkey (WebAuthn) registration by verifying the credential created by the authenticator. Requires elevated access.
 */
export const useVerifyPasskeyHook = () => {
  const verifyPasskey = useWidgetsApiClient<VerifyPasskey200>();

  return useCallback(
    (
      verifyPasskeyRequest: BodyType<VerifyPasskeyRequest>,
      signal?: AbortSignal,
    ) => {
      return verifyPasskey({
        url: `/_widgets/UserProfile/passkeys/verify`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: verifyPasskeyRequest,
        signal,
      });
    },
    [verifyPasskey],
  );
};

export const useVerifyPasskeyMutationOptions = <
  TError = ErrorType<VerifyPasskey400 | VerifyPasskey403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useVerifyPasskeyHook>>>,
    TError,
    { data: BodyType<VerifyPasskeyRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useVerifyPasskeyHook>>>,
  TError,
  { data: BodyType<VerifyPasskeyRequest> },
  TContext
> => {
  const mutationKey = ["verifyPasskey"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const verifyPasskey = useVerifyPasskeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useVerifyPasskeyHook>>>,
    { data: BodyType<VerifyPasskeyRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return verifyPasskey(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyPasskeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useVerifyPasskeyHook>>>
>;
export type VerifyPasskeyMutationBody = BodyType<VerifyPasskeyRequest>;
export type VerifyPasskeyMutationError = ErrorType<
  VerifyPasskey400 | VerifyPasskey403
>;

export const useVerifyPasskey = <
  TError = ErrorType<VerifyPasskey400 | VerifyPasskey403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useVerifyPasskeyHook>>>,
      TError,
      { data: BodyType<VerifyPasskeyRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useVerifyPasskeyHook>>>,
  TError,
  { data: BodyType<VerifyPasskeyRequest> },
  TContext
> => {
  return useMutation(useVerifyPasskeyMutationOptions(options), queryClient);
};

/**
 * Removes the specified passkey from the authenticated user's account. Requires elevated access.
 */
export const useDeletePasskeyHook = () => {
  const deletePasskey = useWidgetsApiClient<DeletePasskey200>();

  return useCallback(
    (passkeyId: string, signal?: AbortSignal) => {
      return deletePasskey({
        url: `/_widgets/UserProfile/passkeys/${passkeyId}`,
        method: "DELETE",
        signal,
      });
    },
    [deletePasskey],
  );
};

export const useDeletePasskeyMutationOptions = <
  TError = ErrorType<DeletePasskey400 | DeletePasskey403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeletePasskeyHook>>>,
    TError,
    { passkeyId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeletePasskeyHook>>>,
  TError,
  { passkeyId: string },
  TContext
> => {
  const mutationKey = ["deletePasskey"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deletePasskey = useDeletePasskeyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeletePasskeyHook>>>,
    { passkeyId: string }
  > = (props) => {
    const { passkeyId } = props ?? {};

    return deletePasskey(passkeyId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePasskeyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeletePasskeyHook>>>
>;

export type DeletePasskeyMutationError = ErrorType<
  DeletePasskey400 | DeletePasskey403
>;

export const useDeletePasskey = <
  TError = ErrorType<DeletePasskey400 | DeletePasskey403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeletePasskeyHook>>>,
      TError,
      { passkeyId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeletePasskeyHook>>>,
  TError,
  { passkeyId: string },
  TContext
> => {
  return useMutation(useDeletePasskeyMutationOptions(options), queryClient);
};

/**
 * Sends a verification email containing a one-time code to the new email address. The change does not take effect until the code is submitted via verify-email-change. Requires elevated access.
 */
export const useSendEmailChangeHook = () => {
  const sendEmailChange = useWidgetsApiClient<SendEmailChangeResponse>();

  return useCallback(
    (
      sendEmailChangeRequest: BodyType<SendEmailChangeRequest>,
      signal?: AbortSignal,
    ) => {
      return sendEmailChange({
        url: `/_widgets/UserProfile/send-email-change`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: sendEmailChangeRequest,
        signal,
      });
    },
    [sendEmailChange],
  );
};

export const useSendEmailChangeMutationOptions = <
  TError = ErrorType<
    | SendEmailChange400
    | SendEmailChange403
    | SendEmailChange409
    | SendEmailChange422
    | SendEmailChange429
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSendEmailChangeHook>>>,
    TError,
    { data: BodyType<SendEmailChangeRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSendEmailChangeHook>>>,
  TError,
  { data: BodyType<SendEmailChangeRequest> },
  TContext
> => {
  const mutationKey = ["sendEmailChange"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const sendEmailChange = useSendEmailChangeHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSendEmailChangeHook>>>,
    { data: BodyType<SendEmailChangeRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return sendEmailChange(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendEmailChangeMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSendEmailChangeHook>>>
>;
export type SendEmailChangeMutationBody = BodyType<SendEmailChangeRequest>;
export type SendEmailChangeMutationError = ErrorType<
  | SendEmailChange400
  | SendEmailChange403
  | SendEmailChange409
  | SendEmailChange422
  | SendEmailChange429
>;

export const useSendEmailChange = <
  TError = ErrorType<
    | SendEmailChange400
    | SendEmailChange403
    | SendEmailChange409
    | SendEmailChange422
    | SendEmailChange429
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSendEmailChangeHook>>>,
      TError,
      { data: BodyType<SendEmailChangeRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSendEmailChangeHook>>>,
  TError,
  { data: BodyType<SendEmailChangeRequest> },
  TContext
> => {
  return useMutation(useSendEmailChangeMutationOptions(options), queryClient);
};

/**
 * Sends a verification email to the authenticated user to confirm their email address.
 */
export const useSendVerificationHook = () => {
  const sendVerification = useWidgetsApiClient<SendVerificationResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return sendVerification({
        url: `/_widgets/UserProfile/send-verification`,
        method: "POST",
        signal,
      });
    },
    [sendVerification],
  );
};

export const useSendVerificationMutationOptions = <
  TError = ErrorType<SendVerification400 | SendVerification403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSendVerificationHook>>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useSendVerificationHook>>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["sendVerification"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const sendVerification = useSendVerificationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useSendVerificationHook>>>,
    void
  > = () => {
    return sendVerification();
  };

  return { mutationFn, ...mutationOptions };
};

export type SendVerificationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSendVerificationHook>>>
>;

export type SendVerificationMutationError = ErrorType<
  SendVerification400 | SendVerification403
>;

export const useSendVerification = <
  TError = ErrorType<SendVerification400 | SendVerification403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useSendVerificationHook>>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useSendVerificationHook>>>,
  TError,
  void,
  TContext
> => {
  return useMutation(useSendVerificationMutationOptions(options), queryClient);
};

/**
 * Returns all currently active sessions for the authenticated user, including device and location information where available.
 */
export const useSessionsHook = () => {
  const sessions = useWidgetsApiClient<ActiveSessionsResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return sessions({
        url: `/_widgets/UserProfile/sessions`,
        method: "GET",
        signal,
      });
    },
    [sessions],
  );
};

export const getSessionsQueryKey = () => {
  return [`/_widgets/UserProfile/sessions`] as const;
};

export const useSessionsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
  TError = ErrorType<Sessions403>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSessionsQueryKey();

  const sessions = useSessionsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>
  > = ({ signal }) => sessions(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SessionsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>
>;
export type SessionsQueryError = ErrorType<Sessions403>;

export function useSessions<
  TData = Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
  TError = ErrorType<Sessions403>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessions<
  TData = Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
  TError = ErrorType<Sessions403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessions<
  TData = Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
  TError = ErrorType<Sessions403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useSessions<
  TData = Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
  TError = ErrorType<Sessions403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSessionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useSessionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Revokes all active sessions for the authenticated user except optionally the current one, signing them out of all other devices.
 */
export const useRevokeAllSessionsHook = () => {
  const revokeAllSessions = useWidgetsApiClient<RevokeAllSessions200>();

  return useCallback(
    (
      revokeAllSessionsRequest: BodyType<RevokeAllSessionsRequest>,
      signal?: AbortSignal,
    ) => {
      return revokeAllSessions({
        url: `/_widgets/UserProfile/sessions/revoke-all`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        data: revokeAllSessionsRequest,
        signal,
      });
    },
    [revokeAllSessions],
  );
};

export const useRevokeAllSessionsMutationOptions = <
  TError = ErrorType<RevokeAllSessions400 | RevokeAllSessions403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRevokeAllSessionsHook>>>,
    TError,
    { data: BodyType<RevokeAllSessionsRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRevokeAllSessionsHook>>>,
  TError,
  { data: BodyType<RevokeAllSessionsRequest> },
  TContext
> => {
  const mutationKey = ["revokeAllSessions"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const revokeAllSessions = useRevokeAllSessionsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRevokeAllSessionsHook>>>,
    { data: BodyType<RevokeAllSessionsRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return revokeAllSessions(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeAllSessionsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRevokeAllSessionsHook>>>
>;
export type RevokeAllSessionsMutationBody = BodyType<RevokeAllSessionsRequest>;
export type RevokeAllSessionsMutationError = ErrorType<
  RevokeAllSessions400 | RevokeAllSessions403
>;

export const useRevokeAllSessions = <
  TError = ErrorType<RevokeAllSessions400 | RevokeAllSessions403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRevokeAllSessionsHook>>>,
      TError,
      { data: BodyType<RevokeAllSessionsRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRevokeAllSessionsHook>>>,
  TError,
  { data: BodyType<RevokeAllSessionsRequest> },
  TContext
> => {
  return useMutation(useRevokeAllSessionsMutationOptions(options), queryClient);
};

/**
 * Revokes a specific active session by ID, signing the user out of that particular device or browser.
 */
export const useRevokeSessionHook = () => {
  const revokeSession = useWidgetsApiClient<RevokeSession200>();

  return useCallback(
    (sessionId: string, signal?: AbortSignal) => {
      return revokeSession({
        url: `/_widgets/UserProfile/sessions/revoke/${sessionId}`,
        method: "DELETE",
        signal,
      });
    },
    [revokeSession],
  );
};

export const useRevokeSessionMutationOptions = <
  TError = ErrorType<RevokeSession400 | RevokeSession403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRevokeSessionHook>>>,
    TError,
    { sessionId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useRevokeSessionHook>>>,
  TError,
  { sessionId: string },
  TContext
> => {
  const mutationKey = ["revokeSession"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const revokeSession = useRevokeSessionHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useRevokeSessionHook>>>,
    { sessionId: string }
  > = (props) => {
    const { sessionId } = props ?? {};

    return revokeSession(sessionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeSessionMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useRevokeSessionHook>>>
>;

export type RevokeSessionMutationError = ErrorType<
  RevokeSession400 | RevokeSession403
>;

export const useRevokeSession = <
  TError = ErrorType<RevokeSession400 | RevokeSession403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useRevokeSessionHook>>>,
      TError,
      { sessionId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useRevokeSessionHook>>>,
  TError,
  { sessionId: string },
  TContext
> => {
  return useMutation(useRevokeSessionMutationOptions(options), queryClient);
};

/**
 * Removes all TOTP factors enrolled for the authenticated user, disabling authenticator app as a second factor. Requires elevated access.
 */
export const useDeleteTotpFactorsHook = () => {
  const deleteTotpFactors = useWidgetsApiClient<DeleteTotpFactors200>();

  return useCallback(
    (signal?: AbortSignal) => {
      return deleteTotpFactors({
        url: `/_widgets/UserProfile/totp-factors`,
        method: "DELETE",
        signal,
      });
    },
    [deleteTotpFactors],
  );
};

export const useDeleteTotpFactorsMutationOptions = <
  TError = ErrorType<DeleteTotpFactors400 | DeleteTotpFactors403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTotpFactorsHook>>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteTotpFactorsHook>>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["deleteTotpFactors"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTotpFactors = useDeleteTotpFactorsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteTotpFactorsHook>>>,
    void
  > = () => {
    return deleteTotpFactors();
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTotpFactorsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTotpFactorsHook>>>
>;

export type DeleteTotpFactorsMutationError = ErrorType<
  DeleteTotpFactors400 | DeleteTotpFactors403
>;

export const useDeleteTotpFactors = <
  TError = ErrorType<DeleteTotpFactors400 | DeleteTotpFactors403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteTotpFactorsHook>>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteTotpFactorsHook>>>,
  TError,
  void,
  TContext
> => {
  return useMutation(useDeleteTotpFactorsMutationOptions(options), queryClient);
};

/**
 * Changes the password for the authenticated user. Requires the current password to be supplied alongside the new password.
 */
export const useUpdatePasswordHook = () => {
  const updatePassword = useWidgetsApiClient<UpdatePassword201>();

  return useCallback(
    (
      updatePasswordRequest: BodyType<UpdatePasswordRequest>,
      signal?: AbortSignal,
    ) => {
      return updatePassword({
        url: `/_widgets/UserProfile/update-password`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: updatePasswordRequest,
        signal,
      });
    },
    [updatePassword],
  );
};

export const useUpdatePasswordMutationOptions = <
  TError = ErrorType<UpdatePassword400 | UpdatePassword403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdatePasswordHook>>>,
    TError,
    { data: BodyType<UpdatePasswordRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdatePasswordHook>>>,
  TError,
  { data: BodyType<UpdatePasswordRequest> },
  TContext
> => {
  const mutationKey = ["updatePassword"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updatePassword = useUpdatePasswordHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdatePasswordHook>>>,
    { data: BodyType<UpdatePasswordRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return updatePassword(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePasswordMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdatePasswordHook>>>
>;
export type UpdatePasswordMutationBody = BodyType<UpdatePasswordRequest>;
export type UpdatePasswordMutationError = ErrorType<
  UpdatePassword400 | UpdatePassword403
>;

export const useUpdatePassword = <
  TError = ErrorType<UpdatePassword400 | UpdatePassword403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdatePasswordHook>>>,
      TError,
      { data: BodyType<UpdatePasswordRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdatePasswordHook>>>,
  TError,
  { data: BodyType<UpdatePasswordRequest> },
  TContext
> => {
  return useMutation(useUpdatePasswordMutationOptions(options), queryClient);
};

/**
 * Verifies the email address of the authenticated user using the code sent via the send-verification endpoint. On success, returns an elevated access token that grants access to sensitive operations such as MFA enrollment and passkey management.
 */
export const useVerifyHook = () => {
  const verify = useWidgetsApiClient<VerifyResponse>();

  return useCallback(
    (verifyRequest: BodyType<VerifyRequest>, signal?: AbortSignal) => {
      return verify({
        url: `/_widgets/UserProfile/verify`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: verifyRequest,
        signal,
      });
    },
    [verify],
  );
};

export const useVerifyMutationOptions = <
  TError = ErrorType<Verify400 | Verify403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useVerifyHook>>>,
    TError,
    { data: BodyType<VerifyRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useVerifyHook>>>,
  TError,
  { data: BodyType<VerifyRequest> },
  TContext
> => {
  const mutationKey = ["verify"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const verify = useVerifyHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useVerifyHook>>>,
    { data: BodyType<VerifyRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return verify(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useVerifyHook>>>
>;
export type VerifyMutationBody = BodyType<VerifyRequest>;
export type VerifyMutationError = ErrorType<Verify400 | Verify403>;

export const useVerify = <
  TError = ErrorType<Verify400 | Verify403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useVerifyHook>>>,
      TError,
      { data: BodyType<VerifyRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useVerifyHook>>>,
  TError,
  { data: BodyType<VerifyRequest> },
  TContext
> => {
  return useMutation(useVerifyMutationOptions(options), queryClient);
};

/**
 * Verifies a pending email change using the one-time code sent via send-email-change. On success, updates the user’s email address and returns the updated profile along with an elevated access token that grants access to sensitive operations such as setting a password.
 */
export const useVerifyEmailChangeHook = () => {
  const verifyEmailChange = useWidgetsApiClient<VerifyEmailChangeResponse>();

  return useCallback(
    (
      verifyEmailChangeRequest: BodyType<VerifyEmailChangeRequest>,
      signal?: AbortSignal,
    ) => {
      return verifyEmailChange({
        url: `/_widgets/UserProfile/verify-email-change`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: verifyEmailChangeRequest,
        signal,
      });
    },
    [verifyEmailChange],
  );
};

export const useVerifyEmailChangeMutationOptions = <
  TError = ErrorType<
    | VerifyEmailChange400
    | VerifyEmailChange403
    | VerifyEmailChange409
    | VerifyEmailChange422
    | VerifyEmailChange429
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useVerifyEmailChangeHook>>>,
    TError,
    { data: BodyType<VerifyEmailChangeRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useVerifyEmailChangeHook>>>,
  TError,
  { data: BodyType<VerifyEmailChangeRequest> },
  TContext
> => {
  const mutationKey = ["verifyEmailChange"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const verifyEmailChange = useVerifyEmailChangeHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useVerifyEmailChangeHook>>>,
    { data: BodyType<VerifyEmailChangeRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return verifyEmailChange(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyEmailChangeMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useVerifyEmailChangeHook>>>
>;
export type VerifyEmailChangeMutationBody = BodyType<VerifyEmailChangeRequest>;
export type VerifyEmailChangeMutationError = ErrorType<
  | VerifyEmailChange400
  | VerifyEmailChange403
  | VerifyEmailChange409
  | VerifyEmailChange422
  | VerifyEmailChange429
>;

export const useVerifyEmailChange = <
  TError = ErrorType<
    | VerifyEmailChange400
    | VerifyEmailChange403
    | VerifyEmailChange409
    | VerifyEmailChange422
    | VerifyEmailChange429
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useVerifyEmailChangeHook>>>,
      TError,
      { data: BodyType<VerifyEmailChangeRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useVerifyEmailChangeHook>>>,
  TError,
  { data: BodyType<VerifyEmailChangeRequest> },
  TContext
> => {
  return useMutation(useVerifyEmailChangeMutationOptions(options), queryClient);
};

/**
 * Completes TOTP enrollment by verifying the one-time code generated by the authenticator app. Requires elevated access.
 */
export const useVerifyTotpFactorHook = () => {
  const verifyTotpFactor = useWidgetsApiClient<VerifyTotpFactor200>();

  return useCallback(
    (
      verifyTotpFactorRequest: BodyType<VerifyTotpFactorRequest>,
      signal?: AbortSignal,
    ) => {
      return verifyTotpFactor({
        url: `/_widgets/UserProfile/verify-totp-factor`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: verifyTotpFactorRequest,
        signal,
      });
    },
    [verifyTotpFactor],
  );
};

export const useVerifyTotpFactorMutationOptions = <
  TError = ErrorType<VerifyTotpFactor400 | VerifyTotpFactor403>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useVerifyTotpFactorHook>>>,
    TError,
    { data: BodyType<VerifyTotpFactorRequest> },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useVerifyTotpFactorHook>>>,
  TError,
  { data: BodyType<VerifyTotpFactorRequest> },
  TContext
> => {
  const mutationKey = ["verifyTotpFactor"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const verifyTotpFactor = useVerifyTotpFactorHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useVerifyTotpFactorHook>>>,
    { data: BodyType<VerifyTotpFactorRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return verifyTotpFactor(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type VerifyTotpFactorMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useVerifyTotpFactorHook>>>
>;
export type VerifyTotpFactorMutationBody = BodyType<VerifyTotpFactorRequest>;
export type VerifyTotpFactorMutationError = ErrorType<
  VerifyTotpFactor400 | VerifyTotpFactor403
>;

export const useVerifyTotpFactor = <
  TError = ErrorType<VerifyTotpFactor400 | VerifyTotpFactor403>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useVerifyTotpFactorHook>>>,
      TError,
      { data: BodyType<VerifyTotpFactorRequest> },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useVerifyTotpFactorHook>>>,
  TError,
  { data: BodyType<VerifyTotpFactorRequest> },
  TContext
> => {
  return useMutation(useVerifyTotpFactorMutationOptions(options), queryClient);
};

export const useGetAuditLogStreamHook = () => {
  const getAuditLogStream = useWidgetsApiClient<GetAuditLogStream200>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getAuditLogStream({
        url: `/_widgets/admin-portal/audit-log-stream`,
        method: "GET",
        signal,
      });
    },
    [getAuditLogStream],
  );
};

export const getGetAuditLogStreamQueryKey = () => {
  return [`/_widgets/admin-portal/audit-log-stream`] as const;
};

export const useGetAuditLogStreamQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
  TError = ErrorType<GetAuditLogStream403>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAuditLogStreamQueryKey();

  const getAuditLogStream = useGetAuditLogStreamHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>
  > = ({ signal }) => getAuditLogStream(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAuditLogStreamQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>
>;
export type GetAuditLogStreamQueryError = ErrorType<GetAuditLogStream403>;

export function useGetAuditLogStream<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
  TError = ErrorType<GetAuditLogStream403>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAuditLogStream<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
  TError = ErrorType<GetAuditLogStream403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAuditLogStream<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
  TError = ErrorType<GetAuditLogStream403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetAuditLogStream<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
  TError = ErrorType<GetAuditLogStream403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAuditLogStreamHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetAuditLogStreamQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGenerateAdminPortalLinkHook = () => {
  const generateAdminPortalLink =
    useWidgetsApiClient<GenerateAdminPortalLink201>();

  return useCallback(
    (params: GenerateAdminPortalLinkParams, signal?: AbortSignal) => {
      return generateAdminPortalLink({
        url: `/_widgets/admin-portal/generate-link`,
        method: "POST",
        params,
        signal,
      });
    },
    [generateAdminPortalLink],
  );
};

export const useGenerateAdminPortalLinkMutationOptions = <
  TError = ErrorType<
    | GenerateAdminPortalLink403
    | GenerateAdminPortalLink404
    | GenerateAdminPortalLink500
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useGenerateAdminPortalLinkHook>>>,
    TError,
    { params: GenerateAdminPortalLinkParams },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useGenerateAdminPortalLinkHook>>>,
  TError,
  { params: GenerateAdminPortalLinkParams },
  TContext
> => {
  const mutationKey = ["generateAdminPortalLink"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const generateAdminPortalLink = useGenerateAdminPortalLinkHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useGenerateAdminPortalLinkHook>>>,
    { params: GenerateAdminPortalLinkParams }
  > = (props) => {
    const { params } = props ?? {};

    return generateAdminPortalLink(params);
  };

  return { mutationFn, ...mutationOptions };
};

export type GenerateAdminPortalLinkMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGenerateAdminPortalLinkHook>>>
>;

export type GenerateAdminPortalLinkMutationError = ErrorType<
  | GenerateAdminPortalLink403
  | GenerateAdminPortalLink404
  | GenerateAdminPortalLink500
>;

export const useGenerateAdminPortalLink = <
  TError = ErrorType<
    | GenerateAdminPortalLink403
    | GenerateAdminPortalLink404
    | GenerateAdminPortalLink500
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useGenerateAdminPortalLinkHook>>>,
      TError,
      { params: GenerateAdminPortalLinkParams },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useGenerateAdminPortalLinkHook>>>,
  TError,
  { params: GenerateAdminPortalLinkParams },
  TContext
> => {
  return useMutation(
    useGenerateAdminPortalLinkMutationOptions(options),
    queryClient,
  );
};

export const useListOrganizationDomainsHook = () => {
  const listOrganizationDomains =
    useWidgetsApiClient<ListOrganizationDomains200>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listOrganizationDomains({
        url: `/_widgets/admin-portal/organization-domains`,
        method: "GET",
        signal,
      });
    },
    [listOrganizationDomains],
  );
};

export const getListOrganizationDomainsQueryKey = () => {
  return [`/_widgets/admin-portal/organization-domains`] as const;
};

export const useListOrganizationDomainsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>
  >,
  TError = ErrorType<ListOrganizationDomains403 | ListOrganizationDomains404>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOrganizationDomainsQueryKey();

  const listOrganizationDomains = useListOrganizationDomainsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>
  > = ({ signal }) => listOrganizationDomains(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOrganizationDomainsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>
>;
export type ListOrganizationDomainsQueryError = ErrorType<
  ListOrganizationDomains403 | ListOrganizationDomains404
>;

export function useListOrganizationDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>
  >,
  TError = ErrorType<ListOrganizationDomains403 | ListOrganizationDomains404>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>
  >,
  TError = ErrorType<ListOrganizationDomains403 | ListOrganizationDomains404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganizationDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>
  >,
  TError = ErrorType<ListOrganizationDomains403 | ListOrganizationDomains404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListOrganizationDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>
  >,
  TError = ErrorType<ListOrganizationDomains403 | ListOrganizationDomains404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationDomainsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useListOrganizationDomainsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useDeleteOrganizationDomainHook = () => {
  const deleteOrganizationDomain = useWidgetsApiClient<OrganizationDomain>();

  return useCallback(
    (domainId: string, signal?: AbortSignal) => {
      return deleteOrganizationDomain({
        url: `/_widgets/admin-portal/organization-domains/${domainId}`,
        method: "DELETE",
        signal,
      });
    },
    [deleteOrganizationDomain],
  );
};

export const useDeleteOrganizationDomainMutationOptions = <
  TError = ErrorType<DeleteOrganizationDomain403 | DeleteOrganizationDomain404>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationDomainHook>>>,
    TError,
    { domainId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationDomainHook>>>,
  TError,
  { domainId: string },
  TContext
> => {
  const mutationKey = ["deleteOrganizationDomain"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteOrganizationDomain = useDeleteOrganizationDomainHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationDomainHook>>>,
    { domainId: string }
  > = (props) => {
    const { domainId } = props ?? {};

    return deleteOrganizationDomain(domainId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganizationDomainMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationDomainHook>>>
>;

export type DeleteOrganizationDomainMutationError = ErrorType<
  DeleteOrganizationDomain403 | DeleteOrganizationDomain404
>;

export const useDeleteOrganizationDomain = <
  TError = ErrorType<DeleteOrganizationDomain403 | DeleteOrganizationDomain404>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationDomainHook>>>,
      TError,
      { domainId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationDomainHook>>>,
  TError,
  { domainId: string },
  TContext
> => {
  return useMutation(
    useDeleteOrganizationDomainMutationOptions(options),
    queryClient,
  );
};

export const useReverifyOrganizationDomainHook = () => {
  const reverifyOrganizationDomain = useWidgetsApiClient<OrganizationDomain>();

  return useCallback(
    (domainId: string, signal?: AbortSignal) => {
      return reverifyOrganizationDomain({
        url: `/_widgets/admin-portal/organization-domains/${domainId}/reverify`,
        method: "POST",
        signal,
      });
    },
    [reverifyOrganizationDomain],
  );
};

export const useReverifyOrganizationDomainMutationOptions = <
  TError = ErrorType<
    ReverifyOrganizationDomain403 | ReverifyOrganizationDomain404
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useReverifyOrganizationDomainHook>>>,
    TError,
    { domainId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useReverifyOrganizationDomainHook>>>,
  TError,
  { domainId: string },
  TContext
> => {
  const mutationKey = ["reverifyOrganizationDomain"];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const reverifyOrganizationDomain = useReverifyOrganizationDomainHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useReverifyOrganizationDomainHook>>>,
    { domainId: string }
  > = (props) => {
    const { domainId } = props ?? {};

    return reverifyOrganizationDomain(domainId);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReverifyOrganizationDomainMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useReverifyOrganizationDomainHook>>>
>;

export type ReverifyOrganizationDomainMutationError = ErrorType<
  ReverifyOrganizationDomain403 | ReverifyOrganizationDomain404
>;

export const useReverifyOrganizationDomain = <
  TError = ErrorType<
    ReverifyOrganizationDomain403 | ReverifyOrganizationDomain404
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useReverifyOrganizationDomainHook>>>,
      TError,
      { domainId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useReverifyOrganizationDomainHook>>>,
  TError,
  { domainId: string },
  TContext
> => {
  return useMutation(
    useReverifyOrganizationDomainMutationOptions(options),
    queryClient,
  );
};

export const useListSsoConnectionsHook = () => {
  const listSsoConnections = useWidgetsApiClient<SsoConnection[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listSsoConnections({
        url: `/_widgets/admin-portal/sso-connections`,
        method: "GET",
        signal,
      });
    },
    [listSsoConnections],
  );
};

export const getListSsoConnectionsQueryKey = () => {
  return [`/_widgets/admin-portal/sso-connections`] as const;
};

export const useListSsoConnectionsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSsoConnectionsQueryKey();

  const listSsoConnections = useListSsoConnectionsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>
  > = ({ signal }) => listSsoConnections(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSsoConnectionsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>
>;
export type ListSsoConnectionsQueryError = ErrorType<unknown>;

export function useListSsoConnections<
  TData = Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSsoConnections<
  TData = Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSsoConnections<
  TData = Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListSsoConnections<
  TData = Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListSsoConnectionsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useListSsoConnectionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useListDirectoriesHook = () => {
  const listDirectories = useWidgetsApiClient<DirectoriesResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listDirectories({
        url: `/_widgets/directory-sync/directories`,
        method: "GET",
        signal,
      });
    },
    [listDirectories],
  );
};

export const getListDirectoriesQueryKey = () => {
  return [`/_widgets/directory-sync/directories`] as const;
};

export const useListDirectoriesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
  TError = ErrorType<ListDirectories403 | ListDirectories404>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListDirectoriesQueryKey();

  const listDirectories = useListDirectoriesHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>
  > = ({ signal }) => listDirectories(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListDirectoriesQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>
>;
export type ListDirectoriesQueryError = ErrorType<
  ListDirectories403 | ListDirectories404
>;

export function useListDirectories<
  TData = Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
  TError = ErrorType<ListDirectories403 | ListDirectories404>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListDirectories<
  TData = Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
  TError = ErrorType<ListDirectories403 | ListDirectories404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListDirectories<
  TData = Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
  TError = ErrorType<ListDirectories403 | ListDirectories404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useListDirectories<
  TData = Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
  TError = ErrorType<ListDirectories403 | ListDirectories404>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListDirectoriesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useListDirectoriesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetDirectoryHook = () => {
  const getDirectory = useWidgetsApiClient<Directory>();

  return useCallback(
    (directoryId: string, signal?: AbortSignal) => {
      return getDirectory({
        url: `/_widgets/directory-sync/directories/${directoryId}`,
        method: "GET",
        signal,
      });
    },
    [getDirectory],
  );
};

export const getGetDirectoryQueryKey = (directoryId: string) => {
  return [`/_widgets/directory-sync/directories/${directoryId}`] as const;
};

export const useGetDirectoryQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
  TError = ErrorType<GetDirectory403 | GetDirectory404>,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetDirectoryQueryKey(directoryId);

  const getDirectory = useGetDirectoryHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>
  > = ({ signal }) => getDirectory(directoryId, signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDirectoryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>
>;
export type GetDirectoryQueryError = ErrorType<
  GetDirectory403 | GetDirectory404
>;

export function useGetDirectory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
  TError = ErrorType<GetDirectory403 | GetDirectory404>,
>(
  directoryId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDirectory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
  TError = ErrorType<GetDirectory403 | GetDirectory404>,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDirectory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
  TError = ErrorType<GetDirectory403 | GetDirectory404>,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetDirectory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
  TError = ErrorType<GetDirectory403 | GetDirectory404>,
>(
  directoryId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDirectoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetDirectoryQueryOptions(directoryId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the widget settings for the current environment, including enabled authentication methods and branding configuration.
 */
export const useSettingsHook = () => {
  const settings = useWidgetsApiClient<SettingsResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return settings({ url: `/_widgets/settings`, method: "GET", signal });
    },
    [settings],
  );
};

export const getSettingsQueryKey = () => {
  return [`/_widgets/settings`] as const;
};

export const useSettingsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
  TError = ErrorType<Settings403>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSettingsQueryKey();

  const settings = useSettingsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>
  > = ({ signal }) => settings(signal);

  const customOptions = useWidgetsApiQueryOptions({
    ...queryOptions,
    queryKey,
    queryFn,
  });

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SettingsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>
>;
export type SettingsQueryError = ErrorType<Settings403>;

export function useSettings<
  TData = Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
  TError = ErrorType<Settings403>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSettings<
  TData = Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
  TError = ErrorType<Settings403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSettings<
  TData = Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
  TError = ErrorType<Settings403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useSettings<
  TData = Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
  TError = ErrorType<Settings403>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSettingsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useSettingsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}
